import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { useAuthStore } from '../store/authStore';

export type Language = 'en' | 'vi';

export interface LanguageOption {
  code: Language;
  label: string;
  flag: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
];

// Translation dictionary keys. Keep keys flat and namespaced with dots.
type TranslationDict = Record<string, string>;

const en: TranslationDict = {
  // Common
  'common.save': 'Save',
  'common.saving': 'Saving...',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.loading': 'Loading...',
  'common.error': 'Error',
  'common.success': 'Success',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.retry': 'Try Again',
  'common.open': 'Open',
  'common.signOut': 'Sign Out',
  'common.signIn': 'Sign In',
  'common.signUp': 'Sign Up',
  'common.create': 'Create',

  // Navbar
  'navbar.documents': 'Documents',
  'navbar.load': 'Load',
  'navbar.templates': 'Templates',
  'navbar.pricing': 'Pricing',
  'navbar.docs': 'Docs',
  'navbar.documentation': 'Documentation',
  'navbar.profile': 'Profile',
  'navbar.profileSettings': 'Profile Settings',
  'navbar.loadDocument': 'Load Document',
  'navbar.poweredBy': 'Powered by Intelligence',
  'navbar.toggleDarkMode': 'Toggle dark mode',
  'navbar.toggleMenu': 'Toggle menu',
  'navbar.language': 'Language',
  'navbar.installApp': 'Install App',
  'pwa.bannerTitle': 'Install DocHelper AI',
  'pwa.bannerDesc': 'Add DocHelper AI to your device for quick access and offline use.',
  'pwa.install': 'Install',
  'pwa.later': 'Maybe later',
  'pwa.iosTitle': 'Install DocHelper AI on your Home Screen',
  'pwa.iosStep1': '1. Tap the Share button',
  'pwa.iosStep2': '2. Select "Add to Home Screen"',
  'pwa.iosStep3': '3. Tap "Add" to confirm',
  'pwa.iosClose': 'Got it',
  'pwa.settingsTitle': 'Install App',
  'pwa.settingsDesc': 'Install DocHelper AI on your device for quick access and offline use.',
  'pwa.settingsInstalled': 'DocHelper AI is installed on this device.',
  'pwa.settingsInstall': 'Install Now',
  'pwa.settingsIOS': 'On iOS, tap the Share button and select "Add to Home Screen".',

  // Pricing page
  'pricing.pleaseSignIn': 'Please Sign In',
  'pricing.pleaseSignInMessage': 'You need to be signed in to view pricing and make purchases.',
  'pricing.signInToContinue': 'Sign In to Continue',
  'pricing.title': 'Choose Your Plan',
  'pricing.subtitle': 'Unlock the full potential of DocHelper AI with our premium features and advanced capabilities.',
  'pricing.paymentError': 'Payment Error',
  'pricing.setupRequired': 'Setup Required',
  'pricing.setupDescription': 'It looks like the Stripe payment system needs to be configured. This requires:',
  'pricing.setupStripeKeys': 'Stripe API keys configured in Supabase',
  'pricing.setupEdgeFunctions': 'Edge Functions deployed',
  'pricing.setupWebhooks': 'Webhook endpoints set up',
  'pricing.viewSetupGuide': 'View Setup Guide',
  'pricing.troubleshootingSteps': 'Troubleshooting Steps:',
  'pricing.troubleInternet': 'Ensure you have a stable internet connection',
  'pricing.troubleRefresh': 'Try refreshing the page and attempting again',
  'pricing.troubleCache': 'Clear your browser cache and cookies',
  'pricing.troubleAdBlock': 'Disable any ad blockers or browser extensions',
  'pricing.troubleBrowser': 'Try using a different browser or incognito mode',
  'pricing.debugInfo': 'Debug Information (for support)',
  'pricing.connected': 'Connected',
  'pricing.offline': 'Offline',
  'pricing.supabaseConfigured': 'Configured',
  'pricing.supabaseNotConfigured': 'Not Configured',
  'pricing.perMonth': '/month',
  'pricing.oneTime': 'one-time',
  'pricing.mostPopular': 'MOST POPULAR',
  'pricing.processing': 'Processing...',
  'pricing.subscribeNow': 'Subscribe Now',
  'pricing.buyNow': 'Buy Now',
  'pricing.needHelpChoosing': 'Need Help Choosing?',
  'pricing.needHelpDesc': 'Not sure which plan is right for you? Our team is here to help you find the perfect solution for your document needs.',
  'pricing.contactSupport': 'Contact Support',
  'pricing.tryFreeTemplates': 'Try Free Templates',
  'pricing.paymentSystemSetup': 'Payment System Setup',
  'pricing.forUsers': 'For Users',
  'pricing.paymentIssues': 'Payment Issues?',
  'pricing.paymentIssuesDesc': "If you're experiencing payment issues, please contact our support team for assistance.",
  'pricing.altPaymentMethods': 'Alternative Payment Methods',
  'pricing.altPaymentMethodsDesc': "We're working on additional payment options. Contact us for manual processing.",
  'pricing.faqTitle': 'Frequently Asked Questions',
  'pricing.faqCancelQ': 'Can I cancel anytime?',
  'pricing.faqCancelA': "Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.",
  'pricing.faqRefundsQ': 'Do you offer refunds?',
  'pricing.faqRefundsA': 'We offer a 30-day money-back guarantee for all subscription plans. Contact support for assistance.',
  'pricing.faqMethodsQ': 'What payment methods do you accept?',
  'pricing.faqMethodsA': 'We accept all major credit cards, debit cards, and digital wallets through our secure Stripe integration.',
  'pricing.faqUpgradeQ': 'Can I upgrade or downgrade my plan?',
  'pricing.faqUpgradeA': 'Yes, you can change your plan at any time. Changes will be prorated and reflected in your next billing cycle.',
  'pricing.errOffline': 'You are offline. Please check your internet connection and try again.',
  'pricing.errSessionExpired': 'Your session has expired. Please sign in again.',
  'pricing.errConfig': 'Payment system configuration error. Please contact support.',
  'pricing.errNotDeployed': 'The payment service is not deployed. Please deploy the Stripe Edge Functions first.',
  'pricing.errNotFound': 'Payment service not found. The Stripe Edge Functions may not be deployed.',
  'pricing.errAuthFailed': 'Authentication failed. Please sign in again.',
  'pricing.errNotConfigured': 'Payment system is not configured. Please set up Stripe integration.',
  'pricing.errUnavailable': 'Payment service temporarily unavailable. Please try again in a few minutes.',
  'pricing.errInvalidParams': 'Invalid request parameters. Please try again.',
  'pricing.errInvalidPrice': 'Invalid pricing information. Please contact support.',
  'pricing.errInvalidUrl': 'Invalid checkout URL received from payment service. Please try again.',
  'pricing.errNoUrl': 'No checkout URL received from payment service',
  'pricing.errFailedCheckout': 'Failed to start checkout process',
  'pricing.errNetwork': 'Unable to connect to payment service. Please check your internet connection and try again.',
  'pricing.errNotProperlyConfigured': 'Payment system is not properly configured. Please contact support or check the setup guide.',
  'pricing.errSubscribeConfirm': 'Payment may have succeeded but subscription status is not updated. Would you like to refresh the page to check again?',

  // Documentation page
  'docs.title': 'Documentation',
  'docs.subtitle': 'Comprehensive guides and references for DocHelper AI',
  'docs.backToApp': 'Back to App',
  'docs.searchPlaceholder': 'Search documentation...',
  'docs.allDocs': 'All Docs',
  'docs.userGuide': 'User Guide',
  'docs.userGuideTitle': 'User Guide',
  'docs.userGuideDesc': 'Learn how to use all features of DocHelper AI',
  'docs.viewDocumentation': 'View Documentation',
  'docs.needFullTitle': 'Need the Full Documentation?',
  'docs.needFullDesc': 'Download the complete documentation package in your preferred format. Available formats depend on your subscription plan.',
  'docs.selectFormat': 'Select Format',
  'docs.requiresPlan': 'Requires {plan} plan',
  'docs.downloading': 'Downloading...',
  'docs.downloadPackage': 'Download Documentation Package',
  'docs.currentPlan': 'Current plan',
  'docs.freePlan': 'Free',
  'docs.requiresHigherPlan': 'The {format} format requires a higher plan.',
  'docs.upgradeNow': 'Upgrade now',
  'docs.requiresHigherAlert': 'The {format} format requires a higher subscription plan. Please upgrade to access this format.',
  'docs.failedDownload': 'Failed to download documentation. Please try again.',

  // Pricing features
  'pricing.feat.teamPremiumAI': 'Premium AI document processing',
  'pricing.feat.advancedAI': 'Advanced AI document processing',
  'pricing.feat.basicAI': 'Basic AI document processing',
  'pricing.feat.unlimitedDocs': 'Unlimited documents',
  'pricing.feat.upTo50Docs': 'Up to 50 documents',
  'pricing.feat.unlimitedSize': 'Unlimited document size',
  'pricing.feat.standardSize': 'Standard document size limit',
  'pricing.feat.largeSize': 'Large document size limit',
  'pricing.feat.realtimeCollab': 'Real-time collaboration',
  'pricing.feat.teamCollab': 'Team collaboration',
  'pricing.feat.commentSystem': 'Comment system',
  'pricing.feat.versionHistory': 'Version history tracking',
  'pricing.feat.allExports': 'All export formats (TXT, PDF, DOCX, MD, JSON, HTML, XML, RTF)',
  'pricing.feat.multipleExports': 'Multiple export formats (TXT, PDF, DOCX, MD)',
  'pricing.feat.enhancedExports': 'Enhanced export formats (TXT, PDF, DOCX, MD, JSON, HTML)',
  'pricing.feat.storage100': 'Large cloud storage (100GB)',
  'pricing.feat.storage25': 'Cloud storage (25GB)',
  'pricing.feat.storage5': 'Cloud storage (5GB)',
  'pricing.feat.backups': 'Automatic backups',
  'pricing.feat.apiAccess': 'API Access',
  'pricing.feat.prioritySupport': 'Priority email support',
  'pricing.feat.emailSupport': 'Email support',

  // Templates page
  'templates.title': 'Document Templates',
  'templates.pleaseSignIn': 'Please Sign In',
  'templates.pleaseSignInMessage': 'You need to be signed in to access templates.',
  'templates.requestTemplate': 'Request Template',
  'templates.requestNewTitle': 'Request New Template',
  'templates.requestSuccess': 'Template request submitted successfully!',
  'templates.templateName': 'Template Name',
  'templates.category': 'Category',
  'templates.categoryPlaceholder': 'e.g., legal, business, personal',
  'templates.description': 'Description',
  'templates.descriptionPlaceholder': 'Describe the template you need...',
  'templates.submitting': 'Submitting...',
  'templates.submitRequest': 'Submit Request',
  'templates.searchPlaceholder': 'Search templates...',
  'templates.allCategories': 'All Categories',
  'templates.templatesCount': '{count} template',
  'templates.templatesCountPlural': '{count} templates',
  'templates.showAll': 'Show All',
  'templates.viewCategory': 'View Category',
  'templates.loading': 'Loading templates...',
  'templates.uncategorized': 'Uncategorized',
  'templates.creating': 'Creating...',
  'templates.useTemplate': 'Use Template',
  'templates.noTemplatesFound': 'No Templates Found',
  'templates.tryAdjusting': 'Try adjusting your search or category filter.',
  'templates.notAvailable': 'No templates are available at the moment.',
  'templates.clearFilters': 'Clear Filters',
  'templates.failedLoadTemplate': 'Failed to load template',

  // Load Document page
  'loadDocument.pleaseSignIn': 'Please Sign In',
  'loadDocument.pleaseSignInMessage': 'You need to be signed in to load documents. Sign in to access all features.',
  'loadDocument.signInToContinue': 'Sign In to Continue',
  'loadDocument.title': 'Load Document',
  'loadDocument.subtitle': 'Import your documents from various sources',
  'loadDocument.fromComputer': 'From Computer',
  'loadDocument.fromComputerDesc': 'Upload documents directly from your device',
  'loadDocument.fromSupabase': 'From Supabase',
  'loadDocument.fromSupabaseDesc': 'Access your saved documents',
  'loadDocument.fromUrl': 'From URL',
  'loadDocument.fromUrlDesc': 'Import documents from the web',
  'loadDocument.dragDrop': 'Drag and drop your file here',
  'loadDocument.or': 'or',
  'loadDocument.browseFiles': 'Browse Files',
  'loadDocument.supportedFormats': 'Supported formats',
  'loadDocument.maxFileSize': 'Maximum file size: 10MB',
  'loadDocument.processing': 'Processing...',
  'loadDocument.upload': 'Upload',
  'loadDocument.yourSavedDocuments': 'Your Saved Documents',
  'loadDocument.savedDocumentsDesc': 'Access and manage all your previously saved documents',
  'loadDocument.loading': 'Loading...',
  'loadDocument.viewDocuments': 'View Documents',
  'loadDocument.importFromUrl': 'Import from URL',
  'loadDocument.importFromUrlDesc': 'Enter the URL of the document you want to import',
  'loadDocument.import': 'Import',
  'loadDocument.selectFileFirst': 'Please select a file first',
  'loadDocument.processingFile': 'Processing file...',
  'loadDocument.extractingText': 'Extracting text from {format} file...',
  'loadDocument.savingToDatabase': 'Saving to database...',
  'loadDocument.failedFetchUrl': 'Failed to fetch document from URL',
  'loadDocument.emptyUrl': 'The document at the provided URL is empty',
  'loadDocument.failedLoadUrl': 'Failed to load document from URL',
  'loadDocument.failedUpload': 'Failed to upload file',

  // Auth
  'auth.createAccount': 'Create an Account',
  'auth.signIn': 'Sign In',
  'auth.offline': 'You are currently offline',
  'auth.offlineMessage': 'You are currently offline. Please check your connection.',
  'auth.connectionError': 'Connection error. Please check your connection.',
  'auth.reconnecting': 'Reconnecting to server...',
  'auth.accountCreated': 'Account created successfully!',
  'auth.checkEmail': 'Please check your email to confirm your account before signing in.',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.pleaseWait': 'Please wait...',
  'auth.reconnectingAction': 'Reconnecting...',
  'auth.haveAccount': 'Already have an account? Sign in',
  'auth.noAccount': "Don't have an account? Sign up",
  'auth.welcomeTo': 'Welcome to DocHelper AI',
  'auth.signInToAccess': 'Please sign in to access your documents and templates.',

  // Dashboard
  'dashboard.welcomeBack': 'Welcome back!',
  'dashboard.welcomeBackName': 'Welcome back, {name}!',
  'dashboard.currentPlan': 'Current plan:',
  'dashboard.newDocument': 'New Document',
  'dashboard.documentLimitReached': 'Document limit reached',
  'dashboard.overview': 'Overview',
  'dashboard.analytics': 'Analytics',
  'dashboard.apiAccess': 'API Access',
  'dashboard.recentDocuments': 'Recent Documents',
  'dashboard.noRecentDocuments': 'No recent documents',
  'dashboard.viewAllDocuments': 'View all documents →',
  'dashboard.popularTemplates': 'Popular Templates',
  'dashboard.noTemplatesAvailable': 'No templates available',
  'dashboard.viewAllTemplates': 'View all templates →',
  'dashboard.aiAssistant': 'AI Assistant',
  'dashboard.aiAssistantDesc': 'Let our AI assistant help you create professional documents quickly and easily.',
  'dashboard.aiLevel': 'AI Level:',
  'dashboard.startNewProject': 'Start New Project',
  'dashboard.recentActivity': 'Recent Activity',
  'dashboard.noRecentActivity': 'No recent activity',
  'dashboard.lastUpdated': 'Last updated: {date}',
  'dashboard.updated': 'Updated {date}',
  'dashboard.failedLoadDocuments': 'Failed to load recent documents. Please try again.',
  'dashboard.failedLoadTemplates': 'Failed to load templates. Please try again.',

  // Document Editor
  'editor.untitledDocument': 'Untitled Document',
  'editor.documentTitle': 'Document Title',
  'editor.saving': 'Saving...',
  'editor.saved': 'Saved',
  'editor.savedAt': 'Saved at {time}',
  'editor.saveFailed': 'Save failed',
  'editor.unsavedChanges': 'Unsaved changes',
  'editor.characters': '{count} characters',
  'editor.undo': 'Undo (Ctrl+Z)',
  'editor.redo': 'Redo (Ctrl+Y)',
  'editor.updateDocument': 'Update Document',
  'editor.share': 'Share',
  'editor.export': 'Export',
  'editor.loadingDocument': 'Loading Document',
  'editor.loadingDocumentMessage': 'Please wait while we fetch your document...',
  'editor.failedLoad': 'Failed to Load Document',
  'editor.backToDocuments': 'Back to Documents',
  'editor.pleaseSignIn': 'Please Sign In',
  'editor.pleaseSignInMessage': 'You need to be signed in to access the document editor.',
  'editor.signInToContinue': 'Sign In to Continue',
  'editor.templates': 'Templates',
  'editor.quickFormatting': 'Quick Formatting',
  'editor.heading': 'Heading',
  'editor.bullet': 'Bullet',
  'editor.number': 'Number',
  'editor.quote': 'Quote',
  'editor.quickFill': 'Quick Fill',
  'editor.aiTools': 'AI Tools',
  'editor.aiAssistant': 'AI Assistant',
  'editor.improve': 'Improve',
  'editor.improving': 'Improving...',
  'editor.format': 'Format',
  'editor.formatting': 'Formatting...',
  'editor.summarize': 'Summarize',
  'editor.comments': 'Comments',

  // Export menu
  'export.pdf': 'Export as PDF',
  'export.txt': 'Export as TXT',
  'export.md': 'Export as Markdown',
  'export.json': 'Export as JSON',
  'export.html': 'Export as HTML',
  'export.xml': 'Export as XML',
  'export.rtf': 'Export as RTF',

  // Settings
  'documents.myDocuments': 'My Documents',
  'documents.pleaseSignIn': 'Please Sign In',
  'documents.pleaseSignInMessage': 'You need to be signed in to view your documents.',
  'documents.deleteSelected': 'Delete Selected',
  'documents.newDocument': 'New Document',
  'documents.searchPlaceholder': 'Search documents...',
  'documents.filters': 'Filters',
  'documents.clearAll': 'Clear All',
  'documents.dateRange': 'Date Range',
  'documents.allTime': 'All Time',
  'documents.today': 'Today',
  'documents.pastWeek': 'Past Week',
  'documents.pastMonth': 'Past Month',
  'documents.tags': 'Tags',
  'documents.sortBy': 'Sort By',
  'documents.sortTitle': 'Title',
  'documents.sortCreated': 'Created',
  'documents.sortUpdated': 'Updated',
  'documents.showing': 'Showing {count} of {total} documents',
  'documents.loadingOwned': 'Loading your documents...',
  'documents.loadingShared': 'Loading shared documents...',
  'documents.tableTitle': 'Title',
  'documents.tableRole': 'Role',
  'documents.tableCreated': 'Created',
  'documents.tableLastUpdated': 'Last Updated',
  'documents.tableActions': 'Actions',
  'documents.open': 'Open',
  'documents.noDocumentsFound': 'No documents found',
  'documents.noSearchMatch': 'No documents match your search',
  'documents.createFirstDocument': 'Get started by creating your first document',
  'documents.noSharedDocuments': 'No documents have been shared with you',
  'documents.create': 'Create New Document',
  'documents.sharedWithMe': 'Shared with Me',
  'documents.failedLoad': 'Failed to load documents. Please try again.',
  'documents.failedLoadShared': 'Failed to load shared documents.',

  'settings.title': 'Settings',
  'settings.profileSettings': 'Profile Settings',
  'settings.profileSettingsDesc': 'Manage your account details and preferences',
  'settings.fullName': 'Full Name',
  'settings.enterFullName': 'Enter your full name',
  'settings.saveChanges': 'Save Changes',
  'settings.notifications': 'Notifications',
  'settings.notificationsDesc': 'Configure how you want to receive notifications',
  'settings.docUpdateNotifications': 'Email notifications for document updates',
  'settings.commentNotifications': 'Email notifications for comments',
  'settings.security': 'Security',
  'settings.securityDesc': 'Manage your security preferences',
  'settings.securityMessage': 'Change your password or update security settings',
  'settings.changePassword': 'Change Password',
  'settings.waitResetPassword': 'Wait {seconds}s to reset password',
  'settings.personalCards': 'Personal Information Cards',
  'settings.personalCardsDesc': 'Save your personal details for quick document filling',
  'settings.addNewCard': 'Add New Card',
  'settings.noProfileCards': 'No profile cards yet',
  'settings.noProfileCardsDesc': 'Create a card to quickly fill your personal information into documents',
  'settings.billingSubscription': 'Billing & Subscription',
  'settings.billingDesc': 'Manage your subscription and payment methods',
  'settings.availablePlans': 'Available Plans',
  'settings.billingHistory': 'Billing History',
  'settings.expires': 'Expires',
  'settings.renews': 'Renews',
  'settings.upgradeTo': 'Upgrade to {plan}',
  'settings.cancelSubscription': 'Cancel Subscription',
  'settings.language': 'Language',
  'settings.languageDesc': 'Choose your preferred display language',
  'settings.profileUpdated': 'Profile updated successfully!',
  'settings.notificationsSaved': 'Notification preferences saved!',
  'settings.name': 'Name:',
  'settings.emailLabel': 'Email:',
  'settings.phone': 'Phone:',
  'settings.company': 'Company:',
  'settings.location': 'Location:',
  'settings.loadingProfile': 'Loading profile...',
  'settings.unknownError': 'Unknown error',
  'settings.failedLoadProfile': 'Failed to load profile data: {error}',
  'settings.failedLoadBilling': 'Failed to load billing data: {error}',
  'settings.failedLoadCards': 'Failed to load profile cards: {error}',
  'settings.saving': 'Saving...',
  'settings.waitResetRequest': 'Please wait {seconds} seconds before requesting another password reset.',
  'settings.passwordResetSent': 'Password reset email sent. Please check your inbox.',
  'settings.failedUpdateProfile': 'Failed to update profile',
  'settings.failedSaveNotifications': 'Failed to save notification preferences',
  'settings.confirmCancelSub': 'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.',
  'settings.subscriptionCanceled': 'Subscription canceled. You will have access until the end of your billing period.',
  'settings.failedCancelSub': 'Failed to cancel subscription',
  'settings.freePlan': 'Free',
  'settings.unknownPlan': 'Unknown Plan',
  'settings.contactSupportDetails': 'Contact support for details',
  'settings.subEndsOn': 'Your subscription will end on {date}',
  'settings.moreFeatures': '+{count} more features',
  'settings.cardUpdated': 'Profile card updated successfully!',
  'settings.cardCreated': 'Profile card created successfully!',
  'settings.cardDeleted': 'Profile card deleted successfully!',
  'settings.confirmDeleteCard': 'Are you sure you want to delete this profile card? This action cannot be undone.',
  'settings.failedDeleteCard': 'Failed to delete profile card',
  'settings.editCard': 'Edit card',
  'settings.deleteCard': 'Delete card',
  'settings.perMonth': '/mo',
  'settings.feat.free1': '5 documents',
  'settings.feat.free2': 'Basic templates',
  'settings.feat.free3': 'Email support',
  'settings.feat.basic1': 'AI-powered document assistance',
  'settings.feat.basic2': 'Standard templates library',
  'settings.feat.basic3': 'Export to multiple formats',
  'settings.feat.basic4': 'Basic collaboration tools',
  'settings.feat.basic5': 'Email customer support',
  'settings.feat.basic6': 'Up to 50 documents per month',
  'settings.feat.team1': 'Advanced AI document processing',
  'settings.feat.team2': 'Priority customer support',
  'settings.feat.team3': 'Unlimited document generation',
  'settings.feat.team4': 'Custom templates and formats',
  'settings.feat.team5': 'Team collaboration features',
  'settings.feat.team6': 'API access for integrations',
  'settings.feat.team7': 'Advanced analytics and insights',
  'settings.feat.team8': 'White-label options',
  'settings.feat.ai1': 'Professional AI document processing',
  'settings.feat.ai2': 'Premium templates collection',
  'settings.feat.ai3': 'Advanced export options',
  'settings.feat.ai4': 'Enhanced collaboration features',
  'settings.feat.ai5': 'Priority email support',
  'settings.feat.ai6': 'Unlimited documents',
  'settings.feat.ai7': 'Version history tracking',
  'settings.feat.ai8': 'Advanced formatting tools',

  // Profile card form fields
  'profileCard.fullName': 'Full Name',
  'profileCard.email': 'Email',
  'profileCard.phoneNumber': 'Phone Number',
  'profileCard.companyName': 'Company Name',
  'profileCard.website': 'Website',
  'profileCard.address': 'Address',
  'profileCard.city': 'City',
  'profileCard.state': 'State/Province',
  'profileCard.country': 'Country',
  'profileCard.zipCode': 'Zip/Postal Code',

  // Not Found page
  'notFound.title': 'Page Not Found',
  'notFound.description': "Sorry, we couldn't find the page you're looking for. It might have been moved or doesn't exist.",
  'notFound.goBack': 'Go Back',
  'notFound.returnHome': 'Return Home',

  // Auth Callback page
  'authCallback.completing': 'Completing authentication...',
  'authCallback.waiting': 'Please wait while we set up your account.',

  // Success page
  'success.processing': 'Processing Your Purchase',
  'success.processingDesc': 'Please wait while we confirm your payment and set up your account...',
  'success.upgradeSuccessful': 'Upgrade Successful!',
  'success.paymentSuccessful': 'Payment Successful!',
  'success.upgradeMessage': 'Your subscription has been upgraded! The prorated amount will be charged on your next billing cycle.',
  'success.welcomeMessage': 'Welcome to DocHelper AI! Your subscription is now active.',
  'success.subscription': 'Subscription',
  'success.product': 'Product:',
  'success.amount': 'Amount:',
  'success.type': 'Type:',
  'success.newFeatures': 'Your new features:',
  'success.moreFeatures': '+{count} more features',
  'success.sessionId': 'Session ID:',
  'success.whatsNext': "What's Next?",
  'success.startCreating': 'Start Creating',
  'success.startCreatingDesc': 'Begin creating professional documents with your new AI-powered tools.',
  'success.createDocument': 'Create Document',
  'success.premiumTemplates': 'Premium Templates',
  'success.premiumTemplatesDesc': 'Access your premium templates and advanced features.',
  'success.browseTemplates': 'Browse Templates',
  'success.manageSubscription': 'Manage Subscription',
  'success.manageSubscriptionDesc': 'View your subscription details and billing information.',
  'success.viewSettings': 'View Settings',
  'success.confirmationEmail': 'A confirmation email has been sent to your registered email address with your receipt and subscription details.',
  'success.returnDashboard': 'Return to Dashboard',
  'success.feat1': 'Advanced AI document processing',
  'success.feat2': 'Premium templates collection',
  'success.feat3': 'Enhanced export options',
  'success.feat4': 'Real-time collaboration',
  'success.feat5': 'Priority email support',
  'success.feat6': 'Unlimited documents',

  // ShareDialog
  'share.title': 'Share Document',
  'share.link': 'Share Link',
  'share.withEmail': 'Share with Email',
  'share.emailPlaceholder': 'Enter email address',
  'share.viewer': 'Viewer',
  'share.editor': 'Editor',
  'share.share': 'Share',
  'share.sharing': 'Sharing...',
  'share.sharedWith': 'Shared With',
  'share.loadingUsers': 'Loading shared users...',
  'share.noUsers': 'No users shared with yet',
  'share.remove': 'Remove',
  'share.userNotFound': 'User not found. Please check the email address.',
  'share.failedLoad': 'Failed to load shared users. Please try again.',
  'share.failedShare': 'Failed to share document',
  'share.failedRemove': 'Failed to remove user from sharing',
  'share.failedRole': 'Failed to update user role',
  'share.failedCopy': 'Failed to copy link to clipboard',
  'share.enterEmail': 'Please enter an email address',
  'share.invalidEmail': 'Please enter a valid email address',
  'share.alreadyAccess': 'This user already has access to the document',
  'share.userNotFoundMsg': 'User not found. Please ensure the email is correct and the user has an account.',

  // ProfileCardForm
  'profileCard.editTitle': 'Edit Profile Card',
  'profileCard.createTitle': 'Create Profile Card',
  'profileCard.cardNameRequired': 'Card name is required',
  'profileCard.cardNameExists': 'A card with this name already exists',
  'profileCard.invalidEmail': 'Please enter a valid email address',
  'profileCard.failedSave': 'Failed to save profile card',
  'profileCard.cardName': 'Card Name',
  'profileCard.cardNamePlaceholder': 'e.g., Personal, Work, Business',
  'profileCard.cardNameHint': 'A unique name to identify this card',
  'profileCard.setDefault': 'Set as default card',
  'profileCard.personalInfo': 'Personal Information',
  'profileCard.fullNameLabel': 'Full Name',
  'profileCard.fullNamePlaceholder': 'John Doe',
  'profileCard.emailLabel': 'Email',
  'profileCard.emailPlaceholder': 'john@example.com',
  'profileCard.phoneLabel': 'Phone Number',
  'profileCard.phonePlaceholder': '+1 (555) 123-4567',
  'profileCard.street': 'Street Address',
  'profileCard.streetPlaceholder': '123 Main Street, Apt 4B',
  'profileCard.cityPlaceholder': 'New York',
  'profileCard.statePlaceholder': 'NY',
  'profileCard.professionalInfo': 'Professional Information',
  'profileCard.companyLabel': 'Company Name',
  'profileCard.companyPlaceholder': 'Acme Corp',
  'profileCard.jobTitle': 'Job Title',
  'profileCard.jobTitlePlaceholder': 'Software Engineer',
  'profileCard.additionalInfo': 'Additional Information',
  'profileCard.additionalNotes': 'Additional Notes',
  'profileCard.notesPlaceholder': "Any other information you'd like to include...",
  'profileCard.characters': '{count}/500 characters',
  'profileCard.cancel': 'Cancel',
  'profileCard.saving': 'Saving...',
  'profileCard.saveCard': 'Save Card',
  'profileCard.countryPlaceholder': 'United States',

  // DocumentSettings
  'docSettings.title': 'Document Settings',
  'docSettings.saveSettings': 'Save Settings',
  'docSettings.saving': 'Saving...',
  'docSettings.saved': 'Settings saved successfully!',
  'docSettings.failedSave': 'Failed to save settings',
  'docSettings.visibility': 'Visibility',
  'docSettings.private': 'Private',
  'docSettings.privateDesc': '- Only you and people you share with can access',
  'docSettings.shared': 'Shared',
  'docSettings.sharedDesc': '- Available to specific people or groups',
  'docSettings.public': 'Public',
  'docSettings.publicDesc': '- Anyone with the link can view',
  'docSettings.permissions': 'Permissions',
  'docSettings.allowComments': 'Allow comments',
  'docSettings.allowEdits': 'Allow edits from others',
  'docSettings.requireAuth': 'Require authentication',

  // SubscriptionStatus
  'subStatus.loading': 'Loading subscription...',
  'subStatus.failedLoad': 'Failed to load subscription data',
  'subStatus.freePlan': 'Free Plan',
  'subStatus.upgradeDesc': 'Upgrade to unlock premium features',
  'subStatus.upgradeNow': 'Upgrade Now',
  'subStatus.premiumPlan': 'Premium Plan',
  'subStatus.perMonth': '/month',
  'subStatus.oneTime': 'one-time',
  'subStatus.expires': 'Expires',
  'subStatus.renews': 'Renews',
  'subStatus.paymentMethod': 'Payment method',
  'subStatus.willExpire': 'Your subscription will not renew and will expire on {date}.',

  // ChatWidget
  'chat.title': 'AI ASSISTANT',
  'chat.greeting': "Hello! I'm your AI assistant. How can I help you with your document today?",
  'chat.greetingWithDoc': "I see you're working on a document. How can I help you improve it?",
  'chat.requiresPlan': 'This AI feature requires an AI Plan or higher subscription. Please upgrade your plan to access advanced AI assistance.',
  'chat.noResponse': 'No response generated from AI service. Please try again.',
  'chat.suggestionIntro': "Here's my suggestion for improving your text:",
  'chat.unexpectedError': 'An unexpected error occurred. Please try again later.',
  'chat.errorApology': 'I apologize, but I encountered an error. Please check your connection and try again.',
  'chat.suggestedChange': 'SUGGESTED CHANGE:',
  'chat.applySuggestion': 'APPLY SUGGESTION',
  'chat.placeholder': 'Ask me anything about your document...',

  // AnalyticsDashboard
  'analytics.title': 'Analytics Dashboard',
  'analytics.totalDocuments': 'Total Documents',
  'analytics.thisMonth': 'This Month',
  'analytics.totalWords': 'Total Words',
  'analytics.avgWords': 'Avg Words/Doc',
  'analytics.collaborators': 'Collaborators',
  'analytics.mostActive': 'Most Active',
  'analytics.exports': 'Exports',
  'analytics.views': 'Views',
  'analytics.noData': 'No analytics data available',
  'analytics.sun': 'Sunday',
  'analytics.mon': 'Monday',
  'analytics.tue': 'Tuesday',
  'analytics.wed': 'Wednesday',
  'analytics.thu': 'Thursday',
  'analytics.fri': 'Friday',
  'analytics.sat': 'Saturday',

  // Comments
  'comments.title': 'Comments',
  'comments.addPlaceholder': 'Add a comment...',
  'comments.send': 'Send',
  'comments.noComments': 'No comments yet. Be the first to comment!',
  'comments.failedLoad': 'Failed to load comments',
  'comments.failedAdd': 'Failed to add comment',
  'comments.failedDelete': 'Failed to delete comment',
  'comments.confirmDelete': 'Are you sure you want to delete this comment?',
  'comments.unknownUser': 'Unknown User',
  'comments.connectedUsers': 'Connected Users',
  'comments.noUsersConnected': 'No users currently connected',

  // SaveStatus
  'save.offline': 'Offline - Changes will sync when online',
  'save.saving': 'Saving...',
  'save.saved': 'Saved',
  'save.savedAgo': 'Saved {time}',
  'save.saveFailed': 'Save failed',
  'save.conflict': 'Conflict detected',
  'save.retry': 'Retry',
  'save.pending': '({count} pending)',
  'save.justNow': 'just now',
  'save.secondsAgo': '{count}s ago',
  'save.minutesAgo': '{count}m ago',
  'save.hoursAgo': '{count}h ago',

  // VersionHistory
  'version.loading': 'Loading version history...',
  'version.failedLoad': 'Failed to load version history',
  'version.title': 'Version History',
  'version.count': '{count} version',
  'version.countPlural': '{count} versions',
  'version.refresh': 'Refresh',
  'version.noHistory': 'No version history',
  'version.noHistoryDesc': 'Changes to this document will be tracked here',
  'version.current': 'Current Version',
  'version.view': 'View',
  'version.restore': 'Restore',

  // ThumbnailUpload
  'thumbnail.title': 'Document Thumbnail',
  'thumbnail.invalidFile': 'Please upload a valid image file (PNG, JPEG, WebP, or GIF)',
  'thumbnail.tooLarge': 'File size must be less than 5MB',
  'thumbnail.uploaded': 'Thumbnail uploaded successfully!',
  'thumbnail.failedUpload': 'Failed to upload thumbnail',
  'thumbnail.confirmDelete': 'Are you sure you want to delete this thumbnail?',
  'thumbnail.deleted': 'Thumbnail deleted successfully!',
  'thumbnail.failedDelete': 'Failed to delete thumbnail',
  'thumbnail.replace': 'Replace',
  'thumbnail.delete': 'Delete',
  'thumbnail.deleting': 'Deleting...',
  'thumbnail.uploading': 'Uploading thumbnail...',
  'thumbnail.dropHere': 'Drop an image here or click to upload',
  'thumbnail.formats': 'PNG, JPEG, WebP, or GIF (Max 5MB)',
  'thumbnail.chooseFile': 'Choose File',
  'thumbnail.hint': 'This thumbnail will be displayed in your documents list to help you quickly identify this document.',

  // NotificationBell
  'notif.title': 'Notifications',
  'notif.all': 'All',
  'notif.unread': 'Unread ({count})',
  'notif.markAllRead': 'Mark all as read',
  'notif.clearAll': 'Clear all',
  'notif.noUnread': 'No unread notifications',
  'notif.none': 'No notifications yet',
  'notif.unreadLabel': 'Unread',
  'notif.confirmClear': 'Are you sure you want to delete all notifications?',
  'notif.showing': 'Showing {shown} of {total} notifications',

  // KeyboardShortcutsHelp
  'shortcuts.title': 'Keyboard Shortcuts',
  'shortcuts.pressToView': 'Press {key} at any time to view this help menu',
  'shortcuts.showShortcuts': 'Show keyboard shortcuts',
  'shortcuts.saveDocument': 'Save document',
  'shortcuts.undo': 'Undo',
  'shortcuts.redo': 'Redo',
  'shortcuts.redoAlt': 'Redo (alt)',
  'shortcuts.selectAll': 'Select all',
  'shortcuts.copy': 'Copy',
  'shortcuts.paste': 'Paste',
  'shortcuts.cut': 'Cut',
  'shortcuts.find': 'Find in document',
  'shortcuts.closeDialogs': 'Close dialogs',
  'shortcuts.catGeneral': 'General',
  'shortcuts.catEditing': 'Editing',
  'shortcuts.catNavigation': 'Navigation',

  // FeatureGate
  'featureGate.premiumTitle': 'Premium Feature',
  'featureGate.premiumDesc': 'This feature requires a {plan} plan or higher.',
  'featureGate.currentPlan': 'Current plan: {plan}',
  'featureGate.upgradeNow': 'Upgrade Now',
  'featureGate.advancedAITitle': 'Advanced AI Feature',
  'featureGate.advancedAIDesc': 'Requires {plan} plan for {level} AI processing',
  'featureGate.upgrade': 'Upgrade',
  'featureGate.planFree': 'Free',

  // DocumentSummarizer
  'summarizer.title': 'Document Summary',
  'summarizer.noContent': 'Please add content to your document before summarizing',
  'summarizer.noResult': 'No summary was generated',
  'summarizer.failedGenerate': 'Failed to generate summary',
  'summarizer.length': 'Summary Length',
  'summarizer.brief': 'Brief',
  'summarizer.standard': 'Standard',
  'summarizer.detailed': 'Detailed',
  'summarizer.generating': 'Generating Summary...',
  'summarizer.generate': 'Generate Summary',
  'summarizer.resultLabel': 'Generated Summary',
  'summarizer.copied': 'Copied!',
  'summarizer.copy': 'Copy',
  'summarizer.emptyDesc': 'Select a summary length and click Generate to create a summary of your document.',

  // DocumentLimitWarning
  'docLimit.reached': 'Document Limit Reached',
  'docLimit.approaching': 'Approaching Document Limit',
  'docLimit.usage': "You've used {used} of {limit} documents ({percent}%)",
  'docLimit.upgrade': 'Upgrade',

  // DetachableEditor
  'detach.detachEditor': 'DETACH EDITOR',
  'detach.preview': 'PREVIEW',
  'detach.editor': 'EDITOR',
  'detach.livePreview': 'LIVE PREVIEW',
  'detach.editorDetached': 'EDITOR DETACHED',
  'detach.detachedDesc': 'The editor is now running in a separate floating window for enhanced visibility and seamless multitasking. You can drag, resize, and position it anywhere on your screen.',
  'detach.reattachEditor': 'REATTACH EDITOR',
  'detach.detachedLabel': 'DETACHED EDITOR',
  'detach.titlePlaceholder': 'Document Title',
  'detach.startTyping': 'Start typing...',
  'detach.startTypingDoc': 'Start typing your document...',
  'detach.placeholderTips': "Start typing your document...\n\nPROFESSIONAL FORMATTING TIPS:\n- Use ALL CAPS for headings and important sections\n- Include bullet points for lists\n- Add proper spacing between sections\n- Use formal language and proper grammar\n- Structure your content logically with clear sections\n\nTry selecting a template from the sidebar to get started with a professional format!\n\nUse the Quick Formatting buttons to add:\n   - BOLD HEADINGS with decorative borders\n   - BULLET POINTS for lists\n   - NUMBERED ITEMS for sequences\n   - QUOTES for important statements\n\nClick 'DETACH EDITOR' to open the editor in a separate window for better multitasking!",

  // APIKeyManagement
  'apiKeys.title': 'API Access',
  'apiKeys.desc': 'Manage API keys for programmatic access to your documents',
  'apiKeys.failedLoad': 'Failed to load API keys: {error}',
  'apiKeys.unknownError': 'Unknown error',
  'apiKeys.nameRequired': 'Please provide a name for the API key',
  'apiKeys.created': 'API key created successfully!',
  'apiKeys.failedCreate': 'Failed to create API key: {error}',
  'apiKeys.confirmRevoke': 'Are you sure you want to revoke this API key? This action cannot be undone.',
  'apiKeys.revoked': 'API key revoked successfully',
  'apiKeys.failedRevoke': 'Failed to revoke API key: {error}',
  'apiKeys.confirmDelete': 'Are you sure you want to delete this API key? This action cannot be undone.',
  'apiKeys.deleted': 'API key deleted successfully',
  'apiKeys.failedDelete': 'Failed to delete API key: {error}',
  'apiKeys.copied': 'API key copied to clipboard!',
  'apiKeys.saveKeyTitle': 'Save Your API Key',
  'apiKeys.saveKeyDesc': 'This is the only time you will see this API key. Please copy it now and store it securely.',
  'apiKeys.savedKey': "I've saved my key",
  'apiKeys.generateNew': 'Generate New API Key',
  'apiKeys.keyName': 'API Key Name',
  'apiKeys.keyNamePlaceholder': 'e.g., Production Server, Mobile App',
  'apiKeys.keyNameHint': 'Give your API key a descriptive name to help you identify it later',
  'apiKeys.creating': 'Creating...',
  'apiKeys.createKey': 'Create API Key',
  'apiKeys.cancel': 'Cancel',
  'apiKeys.none': 'No API keys yet. Create one to get started.',
  'apiKeys.yourKeys': 'Your API Keys',
  'apiKeys.unnamedKey': 'Unnamed Key',
  'apiKeys.revokedLabel': 'Revoked',
  'apiKeys.createdLabel': 'Created {date}',
  'apiKeys.lastUsed': 'Last used {date}',
  'apiKeys.revokeTitle': 'Revoke API Key',
  'apiKeys.revoke': 'Revoke',
  'apiKeys.deleteTitle': 'Delete API Key',
  'apiKeys.usingKeyTitle': 'Using Your API Key',
  'apiKeys.usingKeyDesc': 'Include your API key in the Authorization header of your requests:',
  'apiKeys.apiDocsLink': 'Visit the API Documentation for complete integration guides and examples.',

  // Auth error messages
  'auth.unableReconnect': 'Unable to reconnect to the server. Please try again.',
  'auth.stillUnable': 'Still unable to connect. Please check your connection and try again.',
  'auth.checkInternet': 'Please check your internet connection and try again',
  'auth.enterBoth': 'Please enter both email and password',
  'auth.enterValidEmail': 'Please enter a valid email address',
  'auth.unableConnectServer': 'Unable to connect to the server. Please check your connection and try again.',
  'auth.accountExists': 'An account with this email already exists. Please sign in instead.',
  'auth.failedCreate': 'Failed to create account. Please try again.',
  'auth.invalidCredentials': 'Invalid email or password. Please try again.',
  'auth.emailNotConfirmed': 'Your email address has not been confirmed. Please check your inbox (and spam folder) for a confirmation link.',
  'auth.failedSignIn': 'Failed to sign in. Please try again.',
  'auth.requestTimeout': 'Request timed out. Please check your connection and try again.',
  'auth.unexpectedError': 'An unexpected error occurred',

  // Documents partial
  'documents.confirmDelete': 'Are you sure you want to delete {count} document{plural}? This action cannot be undone.',
  'documents.failedDelete': 'Failed to delete documents',

  // LoadDocument partial
  'loadDocument.invalidFile': 'Invalid file',
  'loadDocument.importedDoc': 'Imported Document',

  // Pricing partial
  'pricing.paymentConfigError': 'Payment system configuration error. Please contact support.',
  'pricing.serverError': 'Server error ({status})',

  // Templates partial
  'templates.authRequired': 'Authentication required',

  // Settings date format
  'settings.expiresOn': 'Expires on {date}',
  'settings.renewsOn': 'Renews on {date}',

  // Navbar tagline
  'navbar.tagline': 'Powered by Intelligence',

  // DocumentEditor component errors
  'editor.signInToLoad': 'Please sign in to load documents',
  'editor.sessionInvalid': 'Authentication session invalid. Please sign in again.',
  'editor.noSession': 'No active session. Please sign in again.',
  'editor.docNotFound': 'Document not found or you do not have permission to access it.',
  'editor.authError': 'Authentication error. Please sign in again.',
  'editor.noPermission': 'You do not have permission to access this document.',
  'editor.failedLoadDocMsg': 'Failed to load document: {error}',
  'editor.failedLoadDoc': 'Failed to load document',
  'editor.failedLoadTemplate': 'Failed to load template: {error}',
  'editor.unknownError': 'Unknown error',
  'editor.failedInit': 'Failed to initialize document',
  'editor.signInToSave': 'Please sign in to save documents',
  'editor.failedSave': 'Failed to save document',
  'editor.formatRequiresBasic': 'AI Format feature requires a Basic Plan or higher subscription. Please upgrade to access this feature.',
  'editor.addContentFormat': 'Please add some content to format',
  'editor.failedFormat': 'Failed to format document',
  'editor.improveRequiresAI': 'AI Improve feature requires an AI Plan or higher subscription. Please upgrade to access this feature.',
  'editor.addContentImprove': 'Please add some content to improve',
  'editor.failedImprove': 'Failed to improve document',
  'editor.exportNotSupported': 'Export format {format} not supported',
  'editor.failedExport': 'Failed to export document',
  'editor.versionAlert': 'Version from {date}\n\nTitle: {title}\n\nContent preview: {preview}...',
  'editor.htmlExportRequiresAI': 'HTML export requires AI plan or higher. Please upgrade your subscription.',
  'editor.xmlExportRequiresTeam': 'XML export requires Team plan. Please upgrade your subscription.',
  'editor.rtfExportRequiresTeam': 'RTF export requires Team plan. Please upgrade your subscription.',
  'editor.templateEmpty': 'Template content is empty or could not be extracted',

  // API Documentation page
  'apiDocs.backToSettings': 'Back to Settings',
  'apiDocs.title': 'DocHelper AI API Documentation',
  'apiDocs.subtitle': 'Complete API reference for integrating with DocHelper AI',
  'apiDocs.yourApiKey': 'Your API Key',
  'apiDocs.useKeyToAuthenticate': 'Use this key to authenticate your API requests',
  'apiDocs.manageKeys': 'Manage Keys',
  'apiDocs.unnamedKey': 'Unnamed Key',
  'apiDocs.noApiKeysFound': 'No API Keys Found',
  'apiDocs.needCreateKey': 'You need to create an API key before you can start making API requests.',
  'apiDocs.createApiKey': 'Create API Key',
  'apiDocs.restfulApi': 'RESTful API',
  'apiDocs.standardHttpMethods': 'Standard HTTP methods',
  'apiDocs.fastReliable': 'Fast & Reliable',
  'apiDocs.uptime': '99.9% uptime SLA',
  'apiDocs.secure': 'Secure',
  'apiDocs.apiKeyAuth': 'API key authentication',
  'apiDocs.gettingStarted': 'Getting Started',
  'apiDocs.baseUrl': 'Base URL',
  'apiDocs.forLocalDev': 'For local development:',
  'apiDocs.authentication': 'Authentication',
  'apiDocs.authMethods': 'The API supports two authentication methods. Include the token in the Authorization header:',
  'apiDocs.method1SupabaseJwt': 'Method 1: Supabase JWT Token (Recommended)',
  'apiDocs.method1Desc': 'Sign in with Supabase to get a JWT access token. Tokens expire after 1 hour.',
  'apiDocs.method2CustomKeys': 'Method 2: Custom API Keys',
  'apiDocs.method2Desc': "Generate permanent API keys in Settings. Keys start with dhk_ and don't expire.",
  'apiDocs.securityBestPractices': 'Security Best Practices',
  'apiDocs.securityTip1': 'Never expose your API key in client-side code',
  'apiDocs.securityTip2': 'Store API keys securely using environment variables',
  'apiDocs.securityTip3': 'Rotate keys regularly and immediately if compromised',
  'apiDocs.securityTip4': 'Use HTTPS for all API requests',
  'apiDocs.documentsApi': 'Documents API',
  'apiDocs.retrieveAllDocs': 'Retrieve a list of all documents for the authenticated user.',
  'apiDocs.queryParameters': 'Query Parameters',
  'apiDocs.paramLimit': '(optional) - Number of documents to return (default: 10, max: 100)',
  'apiDocs.paramPage': '(optional) - Page number for pagination (default: 1)',
  'apiDocs.paramSort': '(optional) - Sort order: created_asc, created_desc, updated_asc, updated_desc',
  'apiDocs.exampleRequest': 'Example Request',
  'apiDocs.exampleResponse': 'Example Response',
  'apiDocs.retrieveDocById': 'Retrieve a specific document by ID.',
  'apiDocs.createNewDoc': 'Create a new document.',
  'apiDocs.requestBody': 'Request Body',
  'apiDocs.updateExistingDoc': 'Update an existing document. Only fields included in the request will be updated.',
  'apiDocs.deleteDocPermanently': 'Delete a document permanently.',
  'apiDocs.aiProcessingApi': 'AI Processing API',
  'apiDocs.processDocWithAi': 'Process document content using AI for improvements, summaries, or transformations.',
  'apiDocs.generateSummary': 'Generate a summary of document content.',
  'apiDocs.templatesApi': 'Templates API',
  'apiDocs.retrieveTemplates': 'Retrieve available document templates.',
  'apiDocs.errorCodes': 'Error Codes',
  'apiDocs.err400Label': 'Invalid request parameters',
  'apiDocs.err400Desc': 'The request was malformed or missing required fields.',
  'apiDocs.err401Label': 'Authentication failed',
  'apiDocs.err401Desc': 'Invalid or missing API key.',
  'apiDocs.err403Label': 'Access denied',
  'apiDocs.err403Desc': "Your subscription plan doesn't include access to this resource.",
  'apiDocs.err404Label': 'Resource not found',
  'apiDocs.err404Desc': "The requested document or resource doesn't exist.",
  'apiDocs.err429Label': 'Rate limit exceeded',
  'apiDocs.err429Desc': "You've exceeded your rate limit. Wait before making more requests.",
  'apiDocs.err500Label': 'Server error',
  'apiDocs.err500Desc': 'Something went wrong on our end. Please try again later.',
  'apiDocs.errorResponseFormat': 'Error Response Format',
  'apiDocs.fastApiFormat': "The API uses FastAPI's standard error format:",
  'apiDocs.backwardCompatNote1': 'Note: Some endpoints may also include',
  'apiDocs.backwardCompatNote2': 'for backward compatibility.',
  'apiDocs.rateLimitsPlanned': 'Rate Limits (Planned Feature)',
  'apiDocs.noteLabel': 'Note:',
  'apiDocs.rateLimitNote': 'Rate limiting is a planned feature and not currently enforced. The following shows the planned rate limits for each subscription tier.',
  'apiDocs.plannedRateLimits': 'Planned rate limits based on subscription plan:',
  'apiDocs.freePlan': 'Free Plan',
  'apiDocs.limitedApiAccess': 'Limited API access',
  'apiDocs.proPlan': 'Pro Plan',
  'apiDocs.standardApiAccess': 'Standard API access',
  'apiDocs.enterprisePlan': 'Enterprise Plan',
  'apiDocs.enhancedApiAccess': 'Enhanced API access',
  'apiDocs.rateLimitHeaders': 'Rate Limit Headers (Coming Soon)',
  'apiDocs.rateLimitHeadersDesc': 'When implemented, API responses will include rate limit information:',
  'apiDocs.codeExamples': 'Code Examples',
  'apiDocs.javascriptNode': 'JavaScript / Node.js',
  'apiDocs.jsDesc': 'Using the Supabase client for authentication and fetch for API calls:',
  'apiDocs.python': 'Python',
  'apiDocs.pythonDesc': 'Using the Supabase client for authentication and requests for API calls:',
  'apiDocs.curl': 'cURL',
  'apiDocs.needHelp': 'Need Help?',
  'apiDocs.needHelpDesc': "If you have questions or need assistance with the API, we're here to help:",
  'apiDocs.emailLabel': 'Email:',
  'apiDocs.interactiveApiDocs': 'Interactive API Docs:',
  'apiDocs.alternativeDocs': 'Alternative Docs:',
  'apiDocs.facebook': 'Facebook:',
  'apiDocs.xTwitter': 'X (Twitter):',
  'apiDocs.apiVersion': 'API Version 1.0 • Last updated: January 2025',

  // Debug Subscription
  'debug.subscriptionDebugPanel': 'Subscription Debug Panel',
  'debug.refresh': 'Refresh',
  'debug.debugCheck': 'Debug Check',
  'debug.currentSubStatus': 'Current Subscription Status',
  'debug.debugInformation': 'Debug Information',
  'debug.userId': 'User ID:',
  'debug.email': 'Email:',
  'debug.currentPlan': 'Current Plan:',
  'debug.status': 'Status:',

  // Payment Setup Guide
  'paymentSetup.stripeSecretKeyDesc': 'Your Stripe secret key from the Stripe dashboard',
  'paymentSetup.webhookSecretDesc': 'Webhook endpoint secret for verifying Stripe events',
  'paymentSetup.createStripeAccount': 'Create Stripe Account',
  'paymentSetup.createStripeDesc': 'Sign up for a Stripe account at stripe.com and get your API keys',
  'paymentSetup.configureEnvVars': 'Configure Environment Variables',
  'paymentSetup.configureEnvDesc': 'Add your Stripe keys to Supabase Edge Functions environment',
  'paymentSetup.deployEdgeFunctions': 'Deploy Edge Functions',
  'paymentSetup.deployEdgeDesc': 'Deploy the stripe-checkout and stripe-webhook functions',
  'paymentSetup.setupWebhooks': 'Set up Webhooks',
  'paymentSetup.setupWebhooksDesc': 'Configure Stripe webhooks to handle payment events',
  'paymentSetup.testPayments': 'Test Payments',
  'paymentSetup.testPaymentsDesc': 'Use Stripe test cards to verify the payment flow',
  'paymentSetup.setupRequired': 'Payment System Setup Required',
  'paymentSetup.notConfigured': 'The payment system is not fully configured. Follow the steps below to enable Stripe payments.',
  'paymentSetup.setupSteps': 'Setup Steps',
  'paymentSetup.required': 'Required',
  'paymentSetup.envVariables': 'Environment Variables',
  'paymentSetup.addEnvVars': 'Add these environment variables to your Supabase Edge Functions:',
  'paymentSetup.usefulLinks': 'Useful Links',
  'paymentSetup.stripeApiKeysDocs': 'Stripe API Keys Documentation',
  'paymentSetup.supabaseEdgeSecrets': 'Supabase Edge Functions Secrets',
  'paymentSetup.stripeWebhooksGuide': 'Stripe Webhooks Guide',
  'paymentSetup.needHelp': 'Need Help?',
  'paymentSetup.needHelpDesc': 'If you need assistance setting up the payment system, please refer to the Stripe and Supabase documentation or contact your development team.',

  // Connection Status
  'connection.offline': 'You are offline. Please check your internet connection.',
  'connection.schemaIssue': 'Database schema issue: "documents" table not found.',
  'connection.dbConnectionFailed': 'Database connection failed. Please check your Supabase project status.',
  'connection.authError': 'Authentication error. Please check your API key configuration.',
  'connection.databaseError': 'Database error: {error}',
  'connection.failedConnect': 'Failed to connect to server',
  'connection.connecting': 'Connecting to server...',
  'connection.connectionError': 'Connection error',
  'connection.showDetails': 'Show details',
  'connection.hideDetails': 'Hide details',
  'connection.connected': 'Connected',
  'connection.connectionDetails': 'Connection Details:',
  'connection.commonFixes': 'Common fixes:',
  'connection.fix1': 'Check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY',
  'connection.fix2': 'Verify your Supabase project is active (not paused)',
  'connection.fix3': 'Ensure your internet connection is stable',
  'connection.fix4': 'Try refreshing the page or restarting the server',
};

const vi: TranslationDict = {
  // Common
  'common.save': 'Lưu',
  'common.saving': 'Đang lưu...',
  'common.cancel': 'Hủy',
  'common.delete': 'Xóa',
  'common.edit': 'Sửa',
  'common.loading': 'Đang tải...',
  'common.error': 'Lỗi',
  'common.success': 'Thành công',
  'common.back': 'Quay lại',
  'common.close': 'Đóng',
  'common.retry': 'Thử lại',
  'common.open': 'Mở',
  'common.signOut': 'Đăng xuất',
  'common.signIn': 'Đăng nhập',
  'common.signUp': 'Đăng ký',
  'common.create': 'Tạo',

  // Navbar
  'navbar.documents': 'Tài liệu',
  'navbar.load': 'Tải lên',
  'navbar.templates': 'Mẫu',
  'navbar.pricing': 'Giá',
  'navbar.docs': 'Tài liệu',
  'navbar.documentation': 'Tài liệu hướng dẫn',
  'navbar.profile': 'Hồ sơ',
  'navbar.profileSettings': 'Cài đặt hồ sơ',
  'navbar.loadDocument': 'Tải tài liệu',
  'navbar.poweredBy': 'Được hỗ trợ bởi Trí tuệ Nhân tạo',
  'navbar.toggleDarkMode': 'Chuyển chế độ tối',
  'navbar.toggleMenu': 'Mở/Tắt menu',
  'navbar.language': 'Ngôn ngữ',
  'navbar.installApp': 'Cài đặt ứng dụng',
  'pwa.bannerTitle': 'Cài đặt DocHelper AI',
  'pwa.bannerDesc': 'Thêm DocHelper AI vào thiết bị của bạn để truy cập nhanh và sử dụng ngoại tuyến.',
  'pwa.install': 'Cài đặt',
  'pwa.later': 'Để sau',
  'pwa.iosTitle': 'Cài đặt DocHelper AI trên Màn hình chính',
  'pwa.iosStep1': '1. Nhấn nút Chia sẻ',
  'pwa.iosStep2': '2. Chọn "Thêm vào Màn hình chính"',
  'pwa.iosStep3': '3. Nhấn "Thêm" để xác nhận',
  'pwa.iosClose': 'Đã hiểu',
  'pwa.settingsTitle': 'Cài đặt ứng dụng',
  'pwa.settingsDesc': 'Cài đặt DocHelper AI trên thiết bị của bạn để truy cập nhanh và sử dụng ngoại tuyến.',
  'pwa.settingsInstalled': 'DocHelper AI đã được cài đặt trên thiết bị này.',
  'pwa.settingsInstall': 'Cài đặt ngay',
  'pwa.settingsIOS': 'Trên iOS, nhấn nút Chia sẻ và chọn "Thêm vào Màn hình chính".',

  // Pricing page
  'pricing.pleaseSignIn': 'Vui lòng đăng nhập',
  'pricing.pleaseSignInMessage': 'Bạn cần đăng nhập để xem giá và mua hàng.',
  'pricing.signInToContinue': 'Đăng nhập để tiếp tục',
  'pricing.title': 'Chọn gói của bạn',
  'pricing.subtitle': 'Mở khóa toàn bộ tiềm năng của DocHelper AI với các tính năng cao cấp và nâng cao.',
  'pricing.paymentError': 'Lỗi thanh toán',
  'pricing.setupRequired': 'Cần thiết lập',
  'pricing.setupDescription': 'Có vẻ như hệ thống thanh toán Stripe cần được cấu hình. Điều này yêu cầu:',
  'pricing.setupStripeKeys': 'Khóa API Stripe được cấu hình trong Supabase',
  'pricing.setupEdgeFunctions': 'Edge Functions đã được triển khai',
  'pricing.setupWebhooks': 'Thiết lập điểm cuối Webhook',
  'pricing.viewSetupGuide': 'Xem hướng dẫn thiết lập',
  'pricing.troubleshootingSteps': 'Các bước khắc phục sự cố:',
  'pricing.troubleInternet': 'Đảm bảo bạn có kết nối internet ổn định',
  'pricing.troubleRefresh': 'Thử tải lại trang và thực hiện lại',
  'pricing.troubleCache': 'Xóa bộ nhớ cache và cookie của trình duyệt',
  'pricing.troubleAdBlock': 'Tắt trình chặn quảng cáo hoặc tiện ích mở rộng trình duyệt',
  'pricing.troubleBrowser': 'Thử dùng trình duyệt khác hoặc chế độ ẩn danh',
  'pricing.debugInfo': 'Thông tin gỡ lỗi (cho hỗ trợ)',
  'pricing.connected': 'Đã kết nối',
  'pricing.offline': 'Ngoại tuyến',
  'pricing.supabaseConfigured': 'Đã cấu hình',
  'pricing.supabaseNotConfigured': 'Chưa cấu hình',
  'pricing.perMonth': '/tháng',
  'pricing.oneTime': 'một lần',
  'pricing.mostPopular': 'PHỔ BIẾN NHẤT',
  'pricing.processing': 'Đang xử lý...',
  'pricing.subscribeNow': 'Đăng ký ngay',
  'pricing.buyNow': 'Mua ngay',
  'pricing.needHelpChoosing': 'Cần trợ giúp chọn gói?',
  'pricing.needHelpDesc': 'Không chắc gói nào phù hợp với bạn? Đội ngũ của chúng tôi luôn sẵn sàng giúp bạn tìm giải pháp hoàn hảo cho nhu cầu tài liệu của bạn.',
  'pricing.contactSupport': 'Liên hệ hỗ trợ',
  'pricing.tryFreeTemplates': 'Thử mẫu miễn phí',
  'pricing.paymentSystemSetup': 'Thiết lập hệ thống thanh toán',
  'pricing.forUsers': 'Dành cho người dùng',
  'pricing.paymentIssues': 'Vấn đề thanh toán?',
  'pricing.paymentIssuesDesc': 'Nếu bạn gặp vấn đề thanh toán, vui lòng liên hệ đội ngũ hỗ trợ của chúng tôi để được trợ giúp.',
  'pricing.altPaymentMethods': 'Phương thức thanh toán thay thế',
  'pricing.altPaymentMethodsDesc': 'Chúng tôi đang phát triển thêm các tùy chọn thanh toán. Liên hệ với chúng tôi để xử lý thủ công.',
  'pricing.faqTitle': 'Câu hỏi thường gặp',
  'pricing.faqCancelQ': 'Tôi có thể hủy bất cứ lúc nào không?',
  'pricing.faqCancelA': 'Có, bạn có thể hủy đăng ký bất cứ lúc nào. Bạn vẫn sẽ có quyền truy cập cho đến hết kỳ thanh toán hiện tại.',
  'pricing.faqRefundsQ': 'Bạn có hoàn tiền không?',
  'pricing.faqRefundsA': 'Chúng tôi đảm bảo hoàn tiền trong 30 ngày cho tất cả các gói đăng ký. Liên hệ hỗ trợ để được trợ giúp.',
  'pricing.faqMethodsQ': 'Bạn chấp nhận phương thức thanh toán nào?',
  'pricing.faqMethodsA': 'Chúng tôi chấp nhận tất cả các loại thẻ tín dụng lớn, thẻ ghi nợ và ví điện tử thông qua tích hợp Stripe an toàn.',
  'pricing.faqUpgradeQ': 'Tôi có thể nâng cấp hoặc hạ cấp gói không?',
  'pricing.faqUpgradeA': 'Có, bạn có thể thay đổi gói bất cứ lúc nào. Các thay đổi sẽ được tính theo tỷ lệ và áp dụng cho kỳ thanh toán tiếp theo.',
  'pricing.errOffline': 'Bạn đang ngoại tuyến. Vui lòng kiểm tra kết nối internet và thử lại.',
  'pricing.errSessionExpired': 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.',
  'pricing.errConfig': 'Lỗi cấu hình hệ thống thanh toán. Vui lòng liên hệ hỗ trợ.',
  'pricing.errNotDeployed': 'Dịch vụ thanh toán chưa được triển khai. Vui lòng triển khai Stripe Edge Functions trước.',
  'pricing.errNotFound': 'Không tìm thấy dịch vụ thanh toán. Có thể Stripe Edge Functions chưa được triển khai.',
  'pricing.errAuthFailed': 'Xác thực thất bại. Vui lòng đăng nhập lại.',
  'pricing.errNotConfigured': 'Hệ thống thanh toán chưa được cấu hình. Vui lòng thiết lập tích hợp Stripe.',
  'pricing.errUnavailable': 'Dịch vụ thanh toán tạm thời không khả dụng. Vui lòng thử lại sau vài phút.',
  'pricing.errInvalidParams': 'Tham số yêu cầu không hợp lệ. Vui lòng thử lại.',
  'pricing.errInvalidPrice': 'Thông tin giá không hợp lệ. Vui lòng liên hệ hỗ trợ.',
  'pricing.errInvalidUrl': 'URL thanh toán không hợp lệ từ dịch vụ thanh toán. Vui lòng thử lại.',
  'pricing.errNoUrl': 'Không nhận được URL thanh toán từ dịch vụ thanh toán',
  'pricing.errFailedCheckout': 'Không thể bắt đầu quá trình thanh toán',
  'pricing.errNetwork': 'Không thể kết nối đến dịch vụ thanh toán. Vui lòng kiểm tra kết nối internet và thử lại.',
  'pricing.errNotProperlyConfigured': 'Hệ thống thanh toán chưa được cấu hình đúng. Vui lòng liên hệ hỗ trợ hoặc xem hướng dẫn thiết lập.',
  'pricing.errSubscribeConfirm': 'Thanh toán có thể đã thành công nhưng trạng thái đăng ký chưa được cập nhật. Bạn có muốn tải lại trang để kiểm tra lại không?',

  // Documentation page
  'docs.title': 'Tài liệu hướng dẫn',
  'docs.subtitle': 'Hướng dẫn và tài liệu tham khảo đầy đủ cho DocHelper AI',
  'docs.backToApp': 'Về ứng dụng',
  'docs.searchPlaceholder': 'Tìm kiếm tài liệu...',
  'docs.allDocs': 'Tất cả tài liệu',
  'docs.userGuide': 'Hướng dẫn người dùng',
  'docs.userGuideTitle': 'Hướng dẫn người dùng',
  'docs.userGuideDesc': 'Tìm hiểu cách sử dụng tất cả tính năng của DocHelper AI',
  'docs.viewDocumentation': 'Xem tài liệu',
  'docs.needFullTitle': 'Bạn cần tài liệu đầy đủ?',
  'docs.needFullDesc': 'Tải xuống gói tài liệu đầy đủ theo định dạng bạn muốn. Các định dạng khả dụng tùy thuộc vào gói đăng ký của bạn.',
  'docs.selectFormat': 'Chọn định dạng',
  'docs.requiresPlan': 'Yêu cầu gói {plan}',
  'docs.downloading': 'Đang tải xuống...',
  'docs.downloadPackage': 'Tải xuống gói tài liệu',
  'docs.currentPlan': 'Gói hiện tại',
  'docs.freePlan': 'Miễn phí',
  'docs.requiresHigherPlan': 'Định dạng {format} yêu cầu gói cao hơn.',
  'docs.upgradeNow': 'Nâng cấp ngay',
  'docs.requiresHigherAlert': 'Định dạng {format} yêu cầu gói đăng ký cao hơn. Vui lòng nâng cấp để truy cập định dạng này.',
  'docs.failedDownload': 'Không thể tải xuống tài liệu. Vui lòng thử lại.',

  // Pricing features
  'pricing.feat.teamPremiumAI': 'Xử lý tài liệu AI cao cấp',
  'pricing.feat.advancedAI': 'Xử lý tài liệu AI nâng cao',
  'pricing.feat.basicAI': 'Xử lý tài liệu AI cơ bản',
  'pricing.feat.unlimitedDocs': 'Tài liệu không giới hạn',
  'pricing.feat.upTo50Docs': 'Tối đa 50 tài liệu',
  'pricing.feat.unlimitedSize': 'Kích thước tài liệu không giới hạn',
  'pricing.feat.standardSize': 'Giới hạn kích thước tài liệu tiêu chuẩn',
  'pricing.feat.largeSize': 'Giới hạn kích thước tài liệu lớn',
  'pricing.feat.realtimeCollab': 'Cộng tác theo thời gian thực',
  'pricing.feat.teamCollab': 'Cộng tác nhóm',
  'pricing.feat.commentSystem': 'Hệ thống bình luận',
  'pricing.feat.versionHistory': 'Theo dõi lịch sử phiên bản',
  'pricing.feat.allExports': 'Tất cả định dạng xuất (TXT, PDF, DOCX, MD, JSON, HTML, XML, RTF)',
  'pricing.feat.multipleExports': 'Nhiều định dạng xuất (TXT, PDF, DOCX, MD)',
  'pricing.feat.enhancedExports': 'Định dạng xuất nâng cao (TXT, PDF, DOCX, MD, JSON, HTML)',
  'pricing.feat.storage100': 'Lưu trữ đám mây lớn (100GB)',
  'pricing.feat.storage25': 'Lưu trữ đám mây (25GB)',
  'pricing.feat.storage5': 'Lưu trữ đám mây (5GB)',
  'pricing.feat.backups': 'Sao lưu tự động',
  'pricing.feat.apiAccess': 'Truy cập API',
  'pricing.feat.prioritySupport': 'Hỗ trợ email ưu tiên',
  'pricing.feat.emailSupport': 'Hỗ trợ email',

  // Templates page
  'templates.title': 'Mẫu tài liệu',
  'templates.pleaseSignIn': 'Vui lòng đăng nhập',
  'templates.pleaseSignInMessage': 'Bạn cần đăng nhập để truy cập mẫu.',
  'templates.requestTemplate': 'Yêu cầu mẫu',
  'templates.requestNewTitle': 'Yêu cầu mẫu mới',
  'templates.requestSuccess': 'Yêu cầu mẫu đã được gửi thành công!',
  'templates.templateName': 'Tên mẫu',
  'templates.category': 'Danh mục',
  'templates.categoryPlaceholder': 'vd: pháp lý, kinh doanh, cá nhân',
  'templates.description': 'Mô tả',
  'templates.descriptionPlaceholder': 'Mô tả mẫu bạn cần...',
  'templates.submitting': 'Đang gửi...',
  'templates.submitRequest': 'Gửi yêu cầu',
  'templates.searchPlaceholder': 'Tìm kiếm mẫu...',
  'templates.allCategories': 'Tất cả danh mục',
  'templates.templatesCount': '{count} mẫu',
  'templates.templatesCountPlural': '{count} mẫu',
  'templates.showAll': 'Hiển thị tất cả',
  'templates.viewCategory': 'Xem danh mục',
  'templates.loading': 'Đang tải mẫu...',
  'templates.uncategorized': 'Chưa phân loại',
  'templates.creating': 'Đang tạo...',
  'templates.useTemplate': 'Sử dụng mẫu',
  'templates.noTemplatesFound': 'Không tìm thấy mẫu',
  'templates.tryAdjusting': 'Thử điều chỉnh tìm kiếm hoặc bộ lọc danh mục.',
  'templates.notAvailable': 'Hiện chưa có mẫu nào.',
  'templates.clearFilters': 'Xóa bộ lọc',
  'templates.failedLoadTemplate': 'Không thể tải mẫu',

  // Load Document page
  'loadDocument.pleaseSignIn': 'Vui lòng đăng nhập',
  'loadDocument.pleaseSignInMessage': 'Bạn cần đăng nhập để tải tài liệu. Đăng nhập để truy cập tất cả các tính năng.',
  'loadDocument.signInToContinue': 'Đăng nhập để tiếp tục',
  'loadDocument.title': 'Tải tài liệu',
  'loadDocument.subtitle': 'Nhập tài liệu của bạn từ nhiều nguồn khác nhau',
  'loadDocument.fromComputer': 'Từ máy tính',
  'loadDocument.fromComputerDesc': 'Tải tài liệu trực tiếp từ thiết bị của bạn',
  'loadDocument.fromSupabase': 'Từ Supabase',
  'loadDocument.fromSupabaseDesc': 'Truy cập tài liệu đã lưu của bạn',
  'loadDocument.fromUrl': 'Từ URL',
  'loadDocument.fromUrlDesc': 'Nhập tài liệu từ web',
  'loadDocument.dragDrop': 'Kéo và thả tệp của bạn vào đây',
  'loadDocument.or': 'hoặc',
  'loadDocument.browseFiles': 'Duyệt tệp',
  'loadDocument.supportedFormats': 'Định dạng được hỗ trợ',
  'loadDocument.maxFileSize': 'Kích thước tệp tối đa: 10MB',
  'loadDocument.processing': 'Đang xử lý...',
  'loadDocument.upload': 'Tải lên',
  'loadDocument.yourSavedDocuments': 'Tài liệu đã lưu của bạn',
  'loadDocument.savedDocumentsDesc': 'Truy cập và quản lý tất cả tài liệu đã lưu trước đó',
  'loadDocument.loading': 'Đang tải...',
  'loadDocument.viewDocuments': 'Xem tài liệu',
  'loadDocument.importFromUrl': 'Nhập từ URL',
  'loadDocument.importFromUrlDesc': 'Nhập URL của tài liệu bạn muốn nhập',
  'loadDocument.import': 'Nhập',
  'loadDocument.selectFileFirst': 'Vui lòng chọn tệp trước',
  'loadDocument.processingFile': 'Đang xử lý tệp...',
  'loadDocument.extractingText': 'Đang trích xuất văn bản từ tệp {format}...',
  'loadDocument.savingToDatabase': 'Đang lưu vào cơ sở dữ liệu...',
  'loadDocument.failedFetchUrl': 'Không thể tải tài liệu từ URL',
  'loadDocument.emptyUrl': 'Tài liệu tại URL cung cấp bị trống',
  'loadDocument.failedLoadUrl': 'Không thể tải tài liệu từ URL',
  'loadDocument.failedUpload': 'Không thể tải tệp lên',

  // Auth
  'auth.createAccount': 'Tạo tài khoản',
  'auth.signIn': 'Đăng nhập',
  'auth.offline': 'Bạn đang ngoại tuyến',
  'auth.offlineMessage': 'Bạn đang ngoại tuyến. Vui lòng kiểm tra kết nối.',
  'auth.connectionError': 'Lỗi kết nối. Vui lòng kiểm tra kết nối.',
  'auth.reconnecting': 'Đang kết nối lại với máy chủ...',
  'auth.accountCreated': 'Tạo tài khoản thành công!',
  'auth.checkEmail': 'Vui lòng kiểm tra email để xác nhận tài khoản trước khi đăng nhập.',
  'auth.email': 'Email',
  'auth.password': 'Mật khẩu',
  'auth.pleaseWait': 'Vui lòng đợi...',
  'auth.reconnectingAction': 'Đang kết nối lại...',
  'auth.haveAccount': 'Đã có tài khoản? Đăng nhập',
  'auth.noAccount': 'Chưa có tài khoản? Đăng ký',
  'auth.welcomeTo': 'Chào mừng đến với DocHelper AI',
  'auth.signInToAccess': 'Vui lòng đăng nhập để truy cập tài liệu và mẫu của bạn.',

  // Dashboard
  'dashboard.welcomeBack': 'Chào mừng trở lại!',
  'dashboard.welcomeBackName': 'Chào mừng trở lại, {name}!',
  'dashboard.currentPlan': 'Gói hiện tại:',
  'dashboard.newDocument': 'Tài liệu mới',
  'dashboard.documentLimitReached': 'Đã đạt giới hạn tài liệu',
  'dashboard.overview': 'Tổng quan',
  'dashboard.analytics': 'Phân tích',
  'dashboard.apiAccess': 'Truy cập API',
  'dashboard.recentDocuments': 'Tài liệu gần đây',
  'dashboard.noRecentDocuments': 'Không có tài liệu gần đây',
  'dashboard.viewAllDocuments': 'Xem tất cả tài liệu →',
  'dashboard.popularTemplates': 'Mẫu phổ biến',
  'dashboard.noTemplatesAvailable': 'Không có mẫu nào',
  'dashboard.viewAllTemplates': 'Xem tất cả mẫu →',
  'dashboard.aiAssistant': 'Trợ lý AI',
  'dashboard.aiAssistantDesc': 'Để trợ lý AI giúp bạn tạo tài liệu chuyên nghiệp nhanh chóng và dễ dàng.',
  'dashboard.aiLevel': 'Cấp độ AI:',
  'dashboard.startNewProject': 'Bắt đầu dự án mới',
  'dashboard.recentActivity': 'Hoạt động gần đây',
  'dashboard.noRecentActivity': 'Không có hoạt động gần đây',
  'dashboard.lastUpdated': 'Cập nhật lần cuối: {date}',
  'dashboard.updated': 'Cập nhật {date}',
  'dashboard.failedLoadDocuments': 'Không thể tải tài liệu gần đây. Vui lòng thử lại.',
  'dashboard.failedLoadTemplates': 'Không thể tải mẫu. Vui lòng thử lại.',

  // Document Editor
  'editor.untitledDocument': 'Tài liệu không tiêu đề',
  'editor.documentTitle': 'Tiêu đề tài liệu',
  'editor.saving': 'Đang lưu...',
  'editor.saved': 'Đã lưu',
  'editor.savedAt': 'Đã lưu lúc {time}',
  'editor.saveFailed': 'Lưu thất bại',
  'editor.unsavedChanges': 'Thay đổi chưa lưu',
  'editor.characters': '{count} ký tự',
  'editor.undo': 'Hoàn tác (Ctrl+Z)',
  'editor.redo': 'Làm lại (Ctrl+Y)',
  'editor.updateDocument': 'Cập nhật tài liệu',
  'editor.share': 'Chia sẻ',
  'editor.export': 'Xuất',
  'editor.loadingDocument': 'Đang tải tài liệu',
  'editor.loadingDocumentMessage': 'Vui lòng đợi trong khi chúng tôi tải tài liệu của bạn...',
  'editor.failedLoad': 'Không thể tải tài liệu',
  'editor.backToDocuments': 'Quay lại tài liệu',
  'editor.pleaseSignIn': 'Vui lòng đăng nhập',
  'editor.pleaseSignInMessage': 'Bạn cần đăng nhập để truy cập trình soạn thảo tài liệu.',
  'editor.signInToContinue': 'Đăng nhập để tiếp tục',
  'editor.templates': 'Mẫu',
  'editor.quickFormatting': 'Định dạng nhanh',
  'editor.heading': 'Tiêu đề',
  'editor.bullet': 'Gạch đầu dòng',
  'editor.number': 'Đánh số',
  'editor.quote': 'Trích dẫn',
  'editor.quickFill': 'Điền nhanh',
  'editor.aiTools': 'Công cụ AI',
  'editor.aiAssistant': 'Trợ lý AI',
  'editor.improve': 'Cải thiện',
  'editor.improving': 'Đang cải thiện...',
  'editor.format': 'Định dạng',
  'editor.formatting': 'Đang định dạng...',
  'editor.summarize': 'Tóm tắt',
  'editor.comments': 'Bình luận',

  // Export menu
  'export.pdf': 'Xuất PDF',
  'export.txt': 'Xuất TXT',
  'export.md': 'Xuất Markdown',
  'export.json': 'Xuất JSON',
  'export.html': 'Xuất HTML',
  'export.xml': 'Xuất XML',
  'export.rtf': 'Xuất RTF',

  // Settings
  'documents.myDocuments': 'Tài liệu của tôi',
  'documents.pleaseSignIn': 'Vui lòng đăng nhập',
  'documents.pleaseSignInMessage': 'Bạn cần đăng nhập để xem tài liệu của mình.',
  'documents.deleteSelected': 'Xóa các mục đã chọn',
  'documents.newDocument': 'Tài liệu mới',
  'documents.searchPlaceholder': 'Tìm kiếm tài liệu...',
  'documents.filters': 'Bộ lọc',
  'documents.clearAll': 'Xóa tất cả',
  'documents.dateRange': 'Khoảng thời gian',
  'documents.allTime': 'Tất cả',
  'documents.today': 'Hôm nay',
  'documents.pastWeek': 'Tuần trước',
  'documents.pastMonth': 'Tháng trước',
  'documents.tags': 'Thẻ',
  'documents.sortBy': 'Sắp xếp theo',
  'documents.sortTitle': 'Tiêu đề',
  'documents.sortCreated': 'Ngày tạo',
  'documents.sortUpdated': 'Ngày cập nhật',
  'documents.showing': 'Đang hiển thị {count} trong số {total} tài liệu',
  'documents.loadingOwned': 'Đang tải tài liệu của bạn...',
  'documents.loadingShared': 'Đang tải tài liệu được chia sẻ...',
  'documents.tableTitle': 'Tiêu đề',
  'documents.tableRole': 'Vai trò',
  'documents.tableCreated': 'Ngày tạo',
  'documents.tableLastUpdated': 'Cập nhật lần cuối',
  'documents.tableActions': 'Hành động',
  'documents.open': 'Mở',
  'documents.noDocumentsFound': 'Không tìm thấy tài liệu',
  'documents.noSearchMatch': 'Không có tài liệu phù hợp với tìm kiếm',
  'documents.createFirstDocument': 'Bắt đầu bằng cách tạo tài liệu đầu tiên của bạn',
  'documents.noSharedDocuments': 'Chưa có tài liệu nào được chia sẻ với bạn',
  'documents.create': 'Tạo tài liệu mới',
  'documents.sharedWithMe': 'Được chia sẻ với tôi',
  'documents.failedLoad': 'Không thể tải tài liệu. Vui lòng thử lại.',
  'documents.failedLoadShared': 'Không thể tải tài liệu được chia sẻ.',

  'settings.title': 'Cài đặt',
  'settings.profileSettings': 'Cài đặt hồ sơ',
  'settings.profileSettingsDesc': 'Quản lý thông tin tài khoản và tùy chọn của bạn',
  'settings.fullName': 'Họ và tên',
  'settings.enterFullName': 'Nhập họ và tên của bạn',
  'settings.saveChanges': 'Lưu thay đổi',
  'settings.notifications': 'Thông báo',
  'settings.notificationsDesc': 'Cấu hình cách bạn muốn nhận thông báo',
  'settings.docUpdateNotifications': 'Thông báo email cho cập nhật tài liệu',
  'settings.commentNotifications': 'Thông báo email cho bình luận',
  'settings.security': 'Bảo mật',
  'settings.securityDesc': 'Quản lý tùy chọn bảo mật của bạn',
  'settings.securityMessage': 'Thay đổi mật khẩu hoặc cập nhật cài đặt bảo mật',
  'settings.changePassword': 'Đổi mật khẩu',
  'settings.waitResetPassword': 'Đợi {seconds}s để đặt lại mật khẩu',
  'settings.personalCards': 'Thẻ thông tin cá nhân',
  'settings.personalCardsDesc': 'Lưu thông tin cá nhân để điền nhanh vào tài liệu',
  'settings.addNewCard': 'Thêm thẻ mới',
  'settings.noProfileCards': 'Chưa có thẻ nào',
  'settings.noProfileCardsDesc': 'Tạo thẻ để điền nhanh thông tin cá nhân vào tài liệu',
  'settings.billingSubscription': 'Thanh toán & Đăng ký',
  'settings.billingDesc': 'Quản lý gói đăng ký và phương thức thanh toán',
  'settings.availablePlans': 'Các gói khả dụng',
  'settings.billingHistory': 'Lịch sử thanh toán',
  'settings.expires': 'Hết hạn',
  'settings.renews': 'Gia hạn',
  'settings.upgradeTo': 'Nâng cấp lên {plan}',
  'settings.cancelSubscription': 'Hủy đăng ký',
  'settings.language': 'Ngôn ngữ',
  'settings.languageDesc': 'Chọn ngôn ngữ hiển thị ưa thích',
  'settings.profileUpdated': 'Đã cập nhật hồ sơ thành công!',
  'settings.notificationsSaved': 'Đã lưu tùy chọn thông báo!',
  'settings.name': 'Tên:',
  'settings.emailLabel': 'Email:',
  'settings.phone': 'Điện thoại:',
  'settings.company': 'Công ty:',
  'settings.location': 'Vị trí:',
  'settings.loadingProfile': 'Đang tải hồ sơ...',
  'settings.unknownError': 'Lỗi không xác định',
  'settings.failedLoadProfile': 'Không thể tải dữ liệu hồ sơ: {error}',
  'settings.failedLoadBilling': 'Không thể tải dữ liệu thanh toán: {error}',
  'settings.failedLoadCards': 'Không thể tải thẻ thông tin: {error}',
  'settings.saving': 'Đang lưu...',
  'settings.waitResetRequest': 'Vui lòng đợi {seconds} giây trước khi yêu cầu đặt lại mật khẩu.',
  'settings.passwordResetSent': 'Email đặt lại mật khẩu đã được gửi. Vui lòng kiểm tra hộp thư của bạn.',
  'settings.failedUpdateProfile': 'Không thể cập nhật hồ sơ',
  'settings.failedSaveNotifications': 'Không thể lưu tùy chọn thông báo',
  'settings.confirmCancelSub': 'Bạn có chắc muốn hủy đăng ký? Bạn sẽ mất quyền truy cập các tính năng cao cấp vào cuối kỳ thanh toán.',
  'settings.subscriptionCanceled': 'Đăng ký đã bị hủy. Bạn vẫn có quyền truy cập cho đến hết kỳ thanh toán.',
  'settings.failedCancelSub': 'Không thể hủy đăng ký',
  'settings.freePlan': 'Miễn phí',
  'settings.unknownPlan': 'Gói không xác định',
  'settings.contactSupportDetails': 'Liên hệ hỗ trợ để biết chi tiết',
  'settings.subEndsOn': 'Đăng ký của bạn sẽ kết thúc vào {date}',
  'settings.moreFeatures': '+{count} tính năng khác',
  'settings.cardUpdated': 'Đã cập nhật thẻ thông tin thành công!',
  'settings.cardCreated': 'Đã tạo thẻ thông tin thành công!',
  'settings.cardDeleted': 'Đã xóa thẻ thông tin thành công!',
  'settings.confirmDeleteCard': 'Bạn có chắc muốn xóa thẻ thông tin này? Hành động này không thể hoàn tác.',
  'settings.failedDeleteCard': 'Không thể xóa thẻ thông tin',
  'settings.editCard': 'Sửa thẻ',
  'settings.deleteCard': 'Xóa thẻ',
  'settings.perMonth': '/tháng',
  'settings.feat.free1': '5 tài liệu',
  'settings.feat.free2': 'Mẫu cơ bản',
  'settings.feat.free3': 'Hỗ trợ email',
  'settings.feat.basic1': 'Trợ lý tài liệu bằng AI',
  'settings.feat.basic2': 'Thư viện mẫu tiêu chuẩn',
  'settings.feat.basic3': 'Xuất ra nhiều định dạng',
  'settings.feat.basic4': 'Công cụ cộng tác cơ bản',
  'settings.feat.basic5': 'Hỗ trợ khách hàng qua email',
  'settings.feat.basic6': 'Tối đa 50 tài liệu mỗi tháng',
  'settings.feat.team1': 'Xử lý tài liệu AI nâng cao',
  'settings.feat.team2': 'Hỗ trợ khách hàng ưu tiên',
  'settings.feat.team3': 'Tạo tài liệu không giới hạn',
  'settings.feat.team4': 'Mẫu và định dạng tùy chỉnh',
  'settings.feat.team5': 'Tính năng cộng tác nhóm',
  'settings.feat.team6': 'Truy cập API để tích hợp',
  'settings.feat.team7': 'Phân tích và thông tin chi tiết nâng cao',
  'settings.feat.team8': 'Tùy chọn thương hiệu riêng',
  'settings.feat.ai1': 'Xử lý tài liệu AI chuyên nghiệp',
  'settings.feat.ai2': 'Bộ sưu tập mẫu cao cấp',
  'settings.feat.ai3': 'Tùy chọn xuất nâng cao',
  'settings.feat.ai4': 'Tính năng cộng tác nâng cao',
  'settings.feat.ai5': 'Hỗ trợ email ưu tiên',
  'settings.feat.ai6': 'Tài liệu không giới hạn',
  'settings.feat.ai7': 'Theo dõi lịch sử phiên bản',
  'settings.feat.ai8': 'Công cụ định dạng nâng cao',

  // Profile card form fields
  'profileCard.fullName': 'Họ và tên',
  'profileCard.email': 'Email',
  'profileCard.phoneNumber': 'Số điện thoại',
  'profileCard.companyName': 'Tên công ty',
  'profileCard.website': 'Trang web',
  'profileCard.address': 'Địa chỉ',
  'profileCard.city': 'Thành phố',
  'profileCard.state': 'Tỉnh/Thành',
  'profileCard.country': 'Quốc gia',
  'profileCard.zipCode': 'Mã bưu chính',

  // Not Found page
  'notFound.title': 'Không tìm thấy trang',
  'notFound.description': 'Xin lỗi, chúng tôi không thể tìm thấy trang bạn đang tìm. Trang có thể đã được chuyển hoặc không tồn tại.',
  'notFound.goBack': 'Quay lại',
  'notFound.returnHome': 'Về trang chủ',

  // Auth Callback page
  'authCallback.completing': 'Đang hoàn tất xác thực...',
  'authCallback.waiting': 'Vui lòng đợi trong khi chúng tôi thiết lập tài khoản của bạn.',

  // Success page
  'success.processing': 'Đang xử lý giao dịch',
  'success.processingDesc': 'Vui lòng đợi trong khi chúng tôi xác nhận thanh toán và thiết lập tài khoản của bạn...',
  'success.upgradeSuccessful': 'Nâng cấp thành công!',
  'success.paymentSuccessful': 'Thanh toán thành công!',
  'success.upgradeMessage': 'Đăng ký của bạn đã được nâng cấp! Số tiền chênh lệch sẽ được tính vào kỳ thanh toán tiếp theo.',
  'success.welcomeMessage': 'Chào mừng đến với DocHelper AI! Đăng ký của bạn hiện đã kích hoạt.',
  'success.subscription': 'Đăng ký',
  'success.product': 'Sản phẩm:',
  'success.amount': 'Số tiền:',
  'success.type': 'Loại:',
  'success.newFeatures': 'Các tính năng mới của bạn:',
  'success.moreFeatures': '+{count} tính năng khác',
  'success.sessionId': 'Mã phiên:',
  'success.whatsNext': 'Tiếp theo làm gì?',
  'success.startCreating': 'Bắt đầu tạo',
  'success.startCreatingDesc': 'Bắt đầu tạo tài liệu chuyên nghiệp với các công cụ AI mới của bạn.',
  'success.createDocument': 'Tạo tài liệu',
  'success.premiumTemplates': 'Mẫu cao cấp',
  'success.premiumTemplatesDesc': 'Truy cập mẫu cao cấp và các tính năng nâng cao của bạn.',
  'success.browseTemplates': 'Duyệt mẫu',
  'success.manageSubscription': 'Quản lý đăng ký',
  'success.manageSubscriptionDesc': 'Xem chi tiết đăng ký và thông tin thanh toán của bạn.',
  'success.viewSettings': 'Xem cài đặt',
  'success.confirmationEmail': 'Một email xác nhận đã được gửi đến địa chỉ email đã đăng ký của bạn kèm theo biên lai và chi tiết đăng ký.',
  'success.returnDashboard': 'Quay lại bảng điều khiển',
  'success.feat1': 'Xử lý tài liệu AI nâng cao',
  'success.feat2': 'Bộ sưu tập mẫu cao cấp',
  'success.feat3': 'Tùy chọn xuất nâng cao',
  'success.feat4': 'Cộng tác theo thời gian thực',
  'success.feat5': 'Hỗ trợ email ưu tiên',
  'success.feat6': 'Tài liệu không giới hạn',

  // ShareDialog
  'share.title': 'Chia sẻ tài liệu',
  'share.link': 'Liên kết chia sẻ',
  'share.withEmail': 'Chia sẻ qua Email',
  'share.emailPlaceholder': 'Nhập địa chỉ email',
  'share.viewer': 'Người xem',
  'share.editor': 'Người chỉnh sửa',
  'share.share': 'Chia sẻ',
  'share.sharing': 'Đang chia sẻ...',
  'share.sharedWith': 'Đã chia sẻ với',
  'share.loadingUsers': 'Đang tải người dùng được chia sẻ...',
  'share.noUsers': 'Chưa chia sẻ với ai',
  'share.remove': 'Xóa',
  'share.userNotFound': 'Không tìm thấy người dùng. Vui lòng kiểm tra địa chỉ email.',
  'share.failedLoad': 'Không thể tải người dùng được chia sẻ. Vui lòng thử lại.',
  'share.failedShare': 'Không thể chia sẻ tài liệu',
  'share.failedRemove': 'Không thể xóa người dùng khỏi danh sách chia sẻ',
  'share.failedRole': 'Không thể cập nhật vai trò người dùng',
  'share.failedCopy': 'Không thể sao chép liên kết',
  'share.enterEmail': 'Vui lòng nhập địa chỉ email',
  'share.invalidEmail': 'Vui lòng nhập địa chỉ email hợp lệ',
  'share.alreadyAccess': 'Người dùng này đã có quyền truy cập tài liệu',
  'share.userNotFoundMsg': 'Không tìm thấy người dùng. Vui lòng đảm bảo email đúng và người dùng đã có tài khoản.',

  // ProfileCardForm
  'profileCard.editTitle': 'Chỉnh sửa thẻ thông tin',
  'profileCard.createTitle': 'Tạo thẻ thông tin',
  'profileCard.cardNameRequired': 'Tên thẻ là bắt buộc',
  'profileCard.cardNameExists': 'Một thẻ với tên này đã tồn tại',
  'profileCard.invalidEmail': 'Vui lòng nhập địa chỉ email hợp lệ',
  'profileCard.failedSave': 'Không thể lưu thẻ thông tin',
  'profileCard.cardName': 'Tên thẻ',
  'profileCard.cardNamePlaceholder': 'vd: Cá nhân, Công việc, Doanh nghiệp',
  'profileCard.cardNameHint': 'Một tên duy nhất để nhận dạng thẻ này',
  'profileCard.setDefault': 'Đặt làm thẻ mặc định',
  'profileCard.personalInfo': 'Thông tin cá nhân',
  'profileCard.fullNameLabel': 'Họ và tên',
  'profileCard.fullNamePlaceholder': 'Nguyễn Văn A',
  'profileCard.emailLabel': 'Email',
  'profileCard.emailPlaceholder': 'nguyenvana@example.com',
  'profileCard.phoneLabel': 'Số điện thoại',
  'profileCard.phonePlaceholder': '+84 912 345 678',
  'profileCard.street': 'Địa chỉ đường phố',
  'profileCard.streetPlaceholder': '123 Đường Lê Lợi, Phường 4B',
  'profileCard.cityPlaceholder': 'Hà Nội',
  'profileCard.statePlaceholder': 'HN',
  'profileCard.professionalInfo': 'Thông tin công việc',
  'profileCard.companyLabel': 'Tên công ty',
  'profileCard.companyPlaceholder': 'Công ty Acme',
  'profileCard.jobTitle': 'Chức danh',
  'profileCard.jobTitlePlaceholder': 'Kỹ sư phần mềm',
  'profileCard.additionalInfo': 'Thông tin bổ sung',
  'profileCard.additionalNotes': 'Ghi chú bổ sung',
  'profileCard.notesPlaceholder': 'Thông tin khác bạn muốn bao gồm...',
  'profileCard.characters': '{count}/500 ký tự',
  'profileCard.cancel': 'Hủy',
  'profileCard.saving': 'Đang lưu...',
  'profileCard.saveCard': 'Lưu thẻ',
  'profileCard.countryPlaceholder': 'Việt Nam',

  // DocumentSettings
  'docSettings.title': 'Cài đặt tài liệu',
  'docSettings.saveSettings': 'Lưu cài đặt',
  'docSettings.saving': 'Đang lưu...',
  'docSettings.saved': 'Đã lưu cài đặt thành công!',
  'docSettings.failedSave': 'Không thể lưu cài đặt',
  'docSettings.visibility': 'Hiển thị',
  'docSettings.private': 'Riêng tư',
  'docSettings.privateDesc': '- Chỉ bạn và những người được chia sẻ có thể truy cập',
  'docSettings.shared': 'Đã chia sẻ',
  'docSettings.sharedDesc': '- Có sẵn cho những người hoặc nhóm cụ thể',
  'docSettings.public': 'Công khai',
  'docSettings.publicDesc': '- Bất kỳ ai có liên kết đều có thể xem',
  'docSettings.permissions': 'Quyền truy cập',
  'docSettings.allowComments': 'Cho phép bình luận',
  'docSettings.allowEdits': 'Cho phép người khác chỉnh sửa',
  'docSettings.requireAuth': 'Yêu cầu xác thực',

  // SubscriptionStatus
  'subStatus.loading': 'Đang tải đăng ký...',
  'subStatus.failedLoad': 'Không thể tải dữ liệu đăng ký',
  'subStatus.freePlan': 'Gói miễn phí',
  'subStatus.upgradeDesc': 'Nâng cấp để mở khóa các tính năng cao cấp',
  'subStatus.upgradeNow': 'Nâng cấp ngay',
  'subStatus.premiumPlan': 'Gói cao cấp',
  'subStatus.perMonth': '/tháng',
  'subStatus.oneTime': 'một lần',
  'subStatus.expires': 'Hết hạn',
  'subStatus.renews': 'Gia hạn',
  'subStatus.paymentMethod': 'Phương thức thanh toán',
  'subStatus.willExpire': 'Đăng ký của bạn sẽ không gia hạn và hết hạn vào {date}.',

  // ChatWidget
  'chat.title': 'TRỢ LÝ AI',
  'chat.greeting': 'Xin chào! Tôi là trợ lý AI của bạn. Tôi có thể giúp gì cho tài liệu của bạn hôm nay?',
  'chat.greetingWithDoc': 'Tôi thấy bạn đang làm việc trên một tài liệu. Tôi có thể giúp bạn cải thiện nó như thế nào?',
  'chat.requiresPlan': 'Tính năng AI này yêu cầu gói AI trở lên. Vui lòng nâng cấp gói để truy cập trợ lý AI nâng cao.',
  'chat.noResponse': 'Không có phản hồi từ dịch vụ AI. Vui lòng thử lại.',
  'chat.suggestionIntro': 'Đây là gợi ý của tôi để cải thiện văn bản của bạn:',
  'chat.unexpectedError': 'Đã xảy ra lỗi không mong muốn. Vui lòng thử lại sau.',
  'chat.errorApology': 'Tôi xin lỗi, nhưng đã gặp lỗi. Vui lòng kiểm tra kết nối và thử lại.',
  'chat.suggestedChange': 'THAY ĐỔI ĐỀ XUẤT:',
  'chat.applySuggestion': 'ÁP DỤNG GỢI Ý',
  'chat.placeholder': 'Hỏi tôi bất cứ điều gì về tài liệu của bạn...',

  // AnalyticsDashboard
  'analytics.title': 'Bảng điều khiển phân tích',
  'analytics.totalDocuments': 'Tổng tài liệu',
  'analytics.thisMonth': 'Tháng này',
  'analytics.totalWords': 'Tổng số từ',
  'analytics.avgWords': 'TB từ/tài liệu',
  'analytics.collaborators': 'Cộng tác viên',
  'analytics.mostActive': 'Hoạt động nhiều nhất',
  'analytics.exports': 'Xuất',
  'analytics.views': 'Lượt xem',
  'analytics.noData': 'Không có dữ liệu phân tích',
  'analytics.sun': 'Chủ nhật',
  'analytics.mon': 'Thứ hai',
  'analytics.tue': 'Thứ ba',
  'analytics.wed': 'Thứ tư',
  'analytics.thu': 'Thứ năm',
  'analytics.fri': 'Thứ sáu',
  'analytics.sat': 'Thứ bảy',

  // Comments
  'comments.title': 'Bình luận',
  'comments.addPlaceholder': 'Thêm bình luận...',
  'comments.send': 'Gửi',
  'comments.noComments': 'Chưa có bình luận. Hãy là người đầu tiên bình luận!',
  'comments.failedLoad': 'Không thể tải bình luận',
  'comments.failedAdd': 'Không thể thêm bình luận',
  'comments.failedDelete': 'Không thể xóa bình luận',
  'comments.confirmDelete': 'Bạn có chắc muốn xóa bình luận này?',
  'comments.unknownUser': 'Người dùng không xác định',
  'comments.connectedUsers': 'Người dùng đang kết nối',
  'comments.noUsersConnected': 'Không có người dùng nào đang kết nối',

  // SaveStatus
  'save.offline': 'Ngoại tuyến - Thay đổi sẽ đồng bộ khi trực tuyến',
  'save.saving': 'Đang lưu...',
  'save.saved': 'Đã lưu',
  'save.savedAgo': 'Đã lưu {time}',
  'save.saveFailed': 'Lưu thất bại',
  'save.conflict': 'Phát hiện xung đột',
  'save.retry': 'Thử lại',
  'save.pending': '({count} đang chờ)',
  'save.justNow': 'vừa xong',
  'save.secondsAgo': '{count} giây trước',
  'save.minutesAgo': '{count} phút trước',
  'save.hoursAgo': '{count} giờ trước',

  // VersionHistory
  'version.loading': 'Đang tải lịch sử phiên bản...',
  'version.failedLoad': 'Không thể tải lịch sử phiên bản',
  'version.title': 'Lịch sử phiên bản',
  'version.count': '{count} phiên bản',
  'version.countPlural': '{count} phiên bản',
  'version.refresh': 'Làm mới',
  'version.noHistory': 'Không có lịch sử phiên bản',
  'version.noHistoryDesc': 'Các thay đổi của tài liệu này sẽ được theo dõi tại đây',
  'version.current': 'Phiên bản hiện tại',
  'version.view': 'Xem',
  'version.restore': 'Khôi phục',

  // ThumbnailUpload
  'thumbnail.title': 'Hình thu nhỏ tài liệu',
  'thumbnail.invalidFile': 'Vui lòng tải lên tệp hình ảnh hợp lệ (PNG, JPEG, WebP, hoặc GIF)',
  'thumbnail.tooLarge': 'Kích thước tệp phải nhỏ hơn 5MB',
  'thumbnail.uploaded': 'Đã tải lên hình thu nhỏ thành công!',
  'thumbnail.failedUpload': 'Không thể tải lên hình thu nhỏ',
  'thumbnail.confirmDelete': 'Bạn có chắc muốn xóa hình thu nhỏ này?',
  'thumbnail.deleted': 'Đã xóa hình thu nhỏ thành công!',
  'thumbnail.failedDelete': 'Không thể xóa hình thu nhỏ',
  'thumbnail.replace': 'Thay thế',
  'thumbnail.delete': 'Xóa',
  'thumbnail.deleting': 'Đang xóa...',
  'thumbnail.uploading': 'Đang tải lên hình thu nhỏ...',
  'thumbnail.dropHere': 'Thả hình ảnh vào đây hoặc nhấp để tải lên',
  'thumbnail.formats': 'PNG, JPEG, WebP, hoặc GIF (Tối đa 5MB)',
  'thumbnail.chooseFile': 'Chọn tệp',
  'thumbnail.hint': 'Hình thu nhỏ này sẽ hiển thị trong danh sách tài liệu của bạn để giúp bạn nhanh chóng nhận dạng tài liệu này.',

  // NotificationBell
  'notif.title': 'Thông báo',
  'notif.all': 'Tất cả',
  'notif.unread': 'Chưa đọc ({count})',
  'notif.markAllRead': 'Đánh dấu tất cả là đã đọc',
  'notif.clearAll': 'Xóa tất cả',
  'notif.noUnread': 'Không có thông báo chưa đọc',
  'notif.none': 'Chưa có thông báo',
  'notif.unreadLabel': 'Chưa đọc',
  'notif.confirmClear': 'Bạn có chắc muốn xóa tất cả thông báo?',
  'notif.showing': 'Đang hiển thị {shown} trong số {total} thông báo',

  // KeyboardShortcutsHelp
  'shortcuts.title': 'Phím tắt',
  'shortcuts.pressToView': 'Nhấn {key} bất cứ lúc nào để xem menu trợ giúp này',
  'shortcuts.showShortcuts': 'Hiển thị phím tắt',
  'shortcuts.saveDocument': 'Lưu tài liệu',
  'shortcuts.undo': 'Hoàn tác',
  'shortcuts.redo': 'Làm lại',
  'shortcuts.redoAlt': 'Làm lại (thay thế)',
  'shortcuts.selectAll': 'Chọn tất cả',
  'shortcuts.copy': 'Sao chép',
  'shortcuts.paste': 'Dán',
  'shortcuts.cut': 'Cắt',
  'shortcuts.find': 'Tìm trong tài liệu',
  'shortcuts.closeDialogs': 'Đóng hộp thoại',
  'shortcuts.catGeneral': 'Chung',
  'shortcuts.catEditing': 'Chỉnh sửa',
  'shortcuts.catNavigation': 'Điều hướng',

  // FeatureGate
  'featureGate.premiumTitle': 'Tính năng cao cấp',
  'featureGate.premiumDesc': 'Tính năng này yêu cầu gói {plan} trở lên.',
  'featureGate.currentPlan': 'Gói hiện tại: {plan}',
  'featureGate.upgradeNow': 'Nâng cấp ngay',
  'featureGate.advancedAITitle': 'Tính năng AI nâng cao',
  'featureGate.advancedAIDesc': 'Yêu cầu gói {plan} cho xử lý AI {level}',
  'featureGate.upgrade': 'Nâng cấp',
  'featureGate.planFree': 'Miễn phí',

  // DocumentSummarizer
  'summarizer.title': 'Tóm tắt tài liệu',
  'summarizer.noContent': 'Vui lòng thêm nội dung vào tài liệu trước khi tóm tắt',
  'summarizer.noResult': 'Không tạo được tóm tắt',
  'summarizer.failedGenerate': 'Không thể tạo tóm tắt',
  'summarizer.length': 'Độ dài tóm tắt',
  'summarizer.brief': 'Ngắn gọn',
  'summarizer.standard': 'Tiêu chuẩn',
  'summarizer.detailed': 'Chi tiết',
  'summarizer.generating': 'Đang tạo tóm tắt...',
  'summarizer.generate': 'Tạo tóm tắt',
  'summarizer.resultLabel': 'Tóm tắt đã tạo',
  'summarizer.copied': 'Đã sao chép!',
  'summarizer.copy': 'Sao chép',
  'summarizer.emptyDesc': 'Chọn độ dài tóm tắt và nhấn Tạo tóm tắt để tạo tóm tắt tài liệu của bạn.',

  // DocumentLimitWarning
  'docLimit.reached': 'Đã đạt giới hạn tài liệu',
  'docLimit.approaching': 'Sắp đạt giới hạn tài liệu',
  'docLimit.usage': 'Bạn đã dùng {used} trong số {limit} tài liệu ({percent}%)',
  'docLimit.upgrade': 'Nâng cấp',

  // DetachableEditor
  'detach.detachEditor': 'TÁCH TRÌNH SOẠN THẢO',
  'detach.preview': 'XEM TRƯỚC',
  'detach.editor': 'TRÌNH SOẠN THẢO',
  'detach.livePreview': 'XEM TRƯỚC TRỰC TIẾP',
  'detach.editorDetached': 'TRÌNH SOẠN THẢO ĐÃ TÁCH',
  'detach.detachedDesc': 'Trình soạn thảo hiện đang chạy trong một cửa sổ nổi riêng biệt để hiển thị tốt hơn và đa nhiệm liền mạch. Bạn có thể kéo, thay đổi kích thước và định vị ở bất kỳ đâu trên màn hình.',
  'detach.reattachEditor': 'GẮN LẠI TRÌNH SOẠN THẢO',
  'detach.detachedLabel': 'TRÌNH SOẠN THẢO ĐÃ TÁCH',
  'detach.titlePlaceholder': 'Tiêu đề tài liệu',
  'detach.startTyping': 'Bắt đầu gõ...',
  'detach.startTypingDoc': 'Bắt đầu gõ tài liệu của bạn...',
  'detach.placeholderTips': "Bắt đầu gõ tài liệu của bạn...\n\nMẸO ĐỊNH DẠNG CHUYÊN NGHIỆP:\n- S dụng IN HOA cho tiêu đề và phần quan trọng\n- Thêm dấu đầu dòng cho danh sách\n- Thêm khoảng cách hợp lý giữa các phần\n- S dụng ngôn ngữ trang trọng và ngữ pháp đúng\n- Cấu trúc nội dung logic với các phần rõ ràng\n\nThử chọn mẫu từ thanh bên để bắt đầu với định dạng chuyên nghiệp!\n\nS dụng các nút Định dạng nhanh để thêm:\n   - TIÊU ĐỀ IN ĐẬM với viền trang trí\n   - DẤU ĐẦU DÒNG cho danh sách\n   - MỤC ĐÁNH SỐ cho chuỗi\n   - TRÍCH DẪN cho tuyên bố quan trọng\n\nNhấp 'TÁCH TRÌNH SOẠN THẢO' để mở trình soạn thảo trong cửa sổ riêng để đa nhiệm tốt hơn!",

  // APIKeyManagement
  'apiKeys.title': 'Truy cập API',
  'apiKeys.desc': 'Quản lý khóa API để truy cập tài liệu theo chương trình',
  'apiKeys.failedLoad': 'Không thể tải khóa API: {error}',
  'apiKeys.unknownError': 'Lỗi không xác định',
  'apiKeys.nameRequired': 'Vui lòng cung cấp tên cho khóa API',
  'apiKeys.created': 'Đã tạo khóa API thành công!',
  'apiKeys.failedCreate': 'Không thể tạo khóa API: {error}',
  'apiKeys.confirmRevoke': 'Bạn có chắc muốn thu hồi khóa API này? Hành động này không thể hoàn tác.',
  'apiKeys.revoked': 'Đã thu hồi khóa API thành công',
  'apiKeys.failedRevoke': 'Không thể thu hồi khóa API: {error}',
  'apiKeys.confirmDelete': 'Bạn có chắc muốn xóa khóa API này? Hành động này không thể hoàn tác.',
  'apiKeys.deleted': 'Đã xóa khóa API thành công',
  'apiKeys.failedDelete': 'Không thể xóa khóa API: {error}',
  'apiKeys.copied': 'Đã sao chép khóa API vào clipboard!',
  'apiKeys.saveKeyTitle': 'Lưu khóa API của bạn',
  'apiKeys.saveKeyDesc': 'Đây là lần duy nhất bạn thấy khóa API này. Vui lòng sao chép ngay và lưu trữ an toàn.',
  'apiKeys.savedKey': 'Tôi đã lưu khóa',
  'apiKeys.generateNew': 'Tạo khóa API mới',
  'apiKeys.keyName': 'Tên khóa API',
  'apiKeys.keyNamePlaceholder': 'vd: Máy chủ sản xuất, Ứng dụng di động',
  'apiKeys.keyNameHint': 'Đặt tên mô tả cho khóa API để dễ nhận dạng sau này',
  'apiKeys.creating': 'Đang tạo...',
  'apiKeys.createKey': 'Tạo khóa API',
  'apiKeys.cancel': 'Hủy',
  'apiKeys.none': 'Chưa có khóa API nào. Tạo một khóa để bắt đầu.',
  'apiKeys.yourKeys': 'Khóa API của bạn',
  'apiKeys.unnamedKey': 'Khóa không tên',
  'apiKeys.revokedLabel': 'Đã thu hồi',
  'apiKeys.createdLabel': 'Tạo {date}',
  'apiKeys.lastUsed': 'Sử dụng lần cuối {date}',
  'apiKeys.revokeTitle': 'Thu hồi khóa API',
  'apiKeys.revoke': 'Thu hồi',
  'apiKeys.deleteTitle': 'Xóa khóa API',
  'apiKeys.usingKeyTitle': 'Sử dụng khóa API',
  'apiKeys.usingKeyDesc': 'Bao gồm khóa API trong tiêu đề Authorization của yêu cầu:',
  'apiKeys.apiDocsLink': 'Truy cập Tài liệu API để xem hướng dẫn tích hợp và ví dụ đầy đủ.',

  // Auth error messages
  'auth.unableReconnect': 'Không thể kết nối lại với máy chủ. Vui lòng thử lại.',
  'auth.stillUnable': 'Vẫn không thể kết nối. Vui lòng kiểm tra kết nối và thử lại.',
  'auth.checkInternet': 'Vui lòng kiểm tra kết nối internet và thử lại',
  'auth.enterBoth': 'Vui lòng nhập cả email và mật khẩu',
  'auth.enterValidEmail': 'Vui lòng nhập địa chỉ email hợp lệ',
  'auth.unableConnectServer': 'Không thể kết nối với máy chủ. Vui lòng kiểm tra kết nối và thử lại.',
  'auth.accountExists': 'Tài khoản với email này đã tồn tại. Vui lòng đăng nhập thay vào đó.',
  'auth.failedCreate': 'Không thể tạo tài khoản. Vui lòng thử lại.',
  'auth.invalidCredentials': 'Email hoặc mật khẩu không đúng. Vui lòng thử lại.',
  'auth.emailNotConfirmed': 'Địa chỉ email của bạn chưa được xác nhận. Vui lòng kiểm tra hộp thư (và thư rác) cho liên kết xác nhận.',
  'auth.failedSignIn': 'Không thể đăng nhập. Vui lòng thử lại.',
  'auth.requestTimeout': 'Yêu cầu quá thời gian. Vui lòng kiểm tra kết nối và thử lại.',
  'auth.unexpectedError': 'Đã xảy ra lỗi không mong muốn',

  // Documents partial
  'documents.confirmDelete': 'Bạn có chắc muốn xóa {count} tài liệu{plural}? Hành động này không thể hoàn tác.',
  'documents.failedDelete': 'Không thể xóa tài liệu',

  // LoadDocument partial
  'loadDocument.invalidFile': 'Tệp không hợp lệ',
  'loadDocument.importedDoc': 'Tài liệu đã nhập',

  // Pricing partial
  'pricing.paymentConfigError': 'Lỗi cấu hình hệ thống thanh toán. Vui lòng liên hệ hỗ trợ.',
  'pricing.serverError': 'Lỗi máy chủ ({status})',

  // Templates partial
  'templates.authRequired': 'Yêu cầu xác thực',

  // Settings date format
  'settings.expiresOn': 'Hết hạn vào {date}',
  'settings.renewsOn': 'Gia hạn vào {date}',

  // Navbar tagline
  'navbar.tagline': 'Được hỗ trợ bởi Trí tuệ Nhân tạo',

  // DocumentEditor component errors
  'editor.signInToLoad': 'Vui lòng đăng nhập để tải tài liệu',
  'editor.sessionInvalid': 'Phiên xác thực không hợp lệ. Vui lòng đăng nhập lại.',
  'editor.noSession': 'Không có phiên hoạt động. Vui lòng đăng nhập lại.',
  'editor.docNotFound': 'Không tìm thấy tài liệu hoặc bạn không có quyền truy cập.',
  'editor.authError': 'Lỗi xác thực. Vui lòng đăng nhập lại.',
  'editor.noPermission': 'Bạn không có quyền truy cập tài liệu này.',
  'editor.failedLoadDocMsg': 'Không thể tải tài liệu: {error}',
  'editor.failedLoadDoc': 'Không thể tải tài liệu',
  'editor.failedLoadTemplate': 'Không thể tải mẫu: {error}',
  'editor.unknownError': 'Lỗi không xác định',
  'editor.failedInit': 'Không thể khởi tạo tài liệu',
  'editor.signInToSave': 'Vui lòng đăng nhập để lưu tài liệu',
  'editor.failedSave': 'Không thể lưu tài liệu',
  'editor.formatRequiresBasic': 'Tính năng Định dạng AI yêu cầu gói Basic trở lên. Vui lòng nâng cấp để sử dụng.',
  'editor.addContentFormat': 'Vui lòng thêm nội dung để định dạng',
  'editor.failedFormat': 'Không thể định dạng tài liệu',
  'editor.improveRequiresAI': 'Tính năng Cải thiện AI yêu cầu gói AI trở lên. Vui lòng nâng cấp để sử dụng.',
  'editor.addContentImprove': 'Vui lòng thêm nội dung để cải thiện',
  'editor.failedImprove': 'Không thể cải thiện tài liệu',
  'editor.exportNotSupported': 'Định dạng xuất {format} không được hỗ trợ',
  'editor.failedExport': 'Không thể xuất tài liệu',
  'editor.versionAlert': 'Phiên bản từ {date}\n\nTiêu đề: {title}\n\nXem trước nội dung: {preview}...',
  'editor.htmlExportRequiresAI': 'Xuất HTML yêu cầu gói AI trở lên. Vui lòng nâng cấp đăng ký.',
  'editor.xmlExportRequiresTeam': 'Xuất XML yêu cầu gói Team. Vui lòng nâng cấp đăng ký.',
  'editor.rtfExportRequiresTeam': 'Xuất RTF yêu cầu gói Team. Vui lòng nâng cấp đăng ký.',
  'editor.templateEmpty': 'Nội dung mẫu trống hoặc không thể trích xuất',

  // API Documentation page
  'apiDocs.backToSettings': 'Quay lại Cài đặt',
  'apiDocs.title': 'Tài liệu API DocHelper AI',
  'apiDocs.subtitle': 'Tài liệu tham khảo API đầy đủ để tích hợp với DocHelper AI',
  'apiDocs.yourApiKey': 'Khóa API của bạn',
  'apiDocs.useKeyToAuthenticate': 'Sử dụng khóa này để xác thực các yêu cầu API của bạn',
  'apiDocs.manageKeys': 'Quản lý khóa',
  'apiDocs.unnamedKey': 'Khóa không tên',
  'apiDocs.noApiKeysFound': 'Không tìm thấy khóa API',
  'apiDocs.needCreateKey': 'Bạn cần tạo khóa API trước khi có thể bắt đầu gửi yêu cầu API.',
  'apiDocs.createApiKey': 'Tạo khóa API',
  'apiDocs.restfulApi': 'API RESTful',
  'apiDocs.standardHttpMethods': 'Phương thức HTTP tiêu chuẩn',
  'apiDocs.fastReliable': 'Nhanh & Đáng tin cậy',
  'apiDocs.uptime': '99.9% thời gian hoạt động SLA',
  'apiDocs.secure': 'Bảo mật',
  'apiDocs.apiKeyAuth': 'Xác thực bằng khóa API',
  'apiDocs.gettingStarted': 'Bắt đầu',
  'apiDocs.baseUrl': 'URL cơ sở',
  'apiDocs.forLocalDev': 'Dành cho phát triển cục bộ:',
  'apiDocs.authentication': 'Xác thực',
  'apiDocs.authMethods': 'API hỗ trợ hai phương thức xác thực. Bao gồm token trong tiêu đề Authorization:',
  'apiDocs.method1SupabaseJwt': 'Phương thức 1: Token JWT Supabase (Khuyên dùng)',
  'apiDocs.method1Desc': 'Đăng nhập với Supabase để lấy token truy cập JWT. Token hết hạn sau 1 giờ.',
  'apiDocs.method2CustomKeys': 'Phương thức 2: Khóa API tùy chỉnh',
  'apiDocs.method2Desc': 'Tạo khóa API vĩnh viễn trong Cài đặt. Khóa bắt đầu bằng dhk_ và không hết hạn.',
  'apiDocs.securityBestPractices': 'Thực hành Bảo mật Tốt nhất',
  'apiDocs.securityTip1': 'Không bao giờ để lộ khóa API trong mã phía máy khách',
  'apiDocs.securityTip2': 'Lưu trữ khóa API an toàn bằng biến môi trường',
  'apiDocs.securityTip3': 'Xoay vòng khóa thường xuyên và ngay lập tức nếu bị xâm phạm',
  'apiDocs.securityTip4': 'Sử dụng HTTPS cho tất cả yêu cầu API',
  'apiDocs.documentsApi': 'API Tài liệu',
  'apiDocs.retrieveAllDocs': 'Lấy danh sách tất cả tài liệu của người dùng đã xác thực.',
  'apiDocs.queryParameters': 'Tham số truy vấn',
  'apiDocs.paramLimit': '(tùy chọn) - Số tài liệu trả về (mặc định: 10, tối đa: 100)',
  'apiDocs.paramPage': '(tùy chọn) - Số trang để phân trang (mặc định: 1)',
  'apiDocs.paramSort': '(tùy chọn) - Thứ tự sắp xếp: created_asc, created_desc, updated_asc, updated_desc',
  'apiDocs.exampleRequest': 'Yêu cầu mẫu',
  'apiDocs.exampleResponse': 'Phản hồi mẫu',
  'apiDocs.retrieveDocById': 'Lấy một tài liệu cụ thể theo ID.',
  'apiDocs.createNewDoc': 'Tạo tài liệu mới.',
  'apiDocs.requestBody': 'Nội dung yêu cầu',
  'apiDocs.updateExistingDoc': 'Cập nhật tài liệu hiện có. Chỉ các trường có trong yêu cầu mới được cập nhật.',
  'apiDocs.deleteDocPermanently': 'Xóa tài liệu vĩnh viễn.',
  'apiDocs.aiProcessingApi': 'API Xử lý AI',
  'apiDocs.processDocWithAi': 'Xử lý nội dung tài liệu bằng AI để cải thiện, tóm tắt hoặc chuyển đổi.',
  'apiDocs.generateSummary': 'Tạo tóm tắt nội dung tài liệu.',
  'apiDocs.templatesApi': 'API Mẫu',
  'apiDocs.retrieveTemplates': 'Lấy các mẫu tài liệu có sẵn.',
  'apiDocs.errorCodes': 'Mã lỗi',
  'apiDocs.err400Label': 'Tham số yêu cầu không hợp lệ',
  'apiDocs.err400Desc': 'Yêu cầu bị sai định dạng hoặc thiếu trường bắt buộc.',
  'apiDocs.err401Label': 'Xác thực thất bại',
  'apiDocs.err401Desc': 'Khóa API không hợp lệ hoặc bị thiếu.',
  'apiDocs.err403Label': 'Truy cập bị từ chối',
  'apiDocs.err403Desc': 'Gói đăng ký của bạn không bao gồm quyền truy cập tài nguyên này.',
  'apiDocs.err404Label': 'Không tìm thấy tài nguyên',
  'apiDocs.err404Desc': 'Tài liệu hoặc tài nguyên được yêu cầu không tồn tại.',
  'apiDocs.err429Label': 'Vượt quá giới hạn tỷ lệ',
  'apiDocs.err429Desc': 'Bạn đã vượt quá giới hạn tỷ lệ. Vui lòng đợi trước khi gửi thêm yêu cầu.',
  'apiDocs.err500Label': 'Lỗi máy chủ',
  'apiDocs.err500Desc': 'Đã có lỗi xảy ra từ phía chúng tôi. Vui lòng thử lại sau.',
  'apiDocs.errorResponseFormat': 'Định dạng Phản hồi Lỗi',
  'apiDocs.fastApiFormat': 'API sử dụng định dạng lỗi tiêu chuẩn của FastAPI:',
  'apiDocs.backwardCompatNote1': 'Lưu ý: Một số điểm cuối cũng có thể bao gồm',
  'apiDocs.backwardCompatNote2': 'để tương thích ngược.',
  'apiDocs.rateLimitsPlanned': 'Giới hạn Tỷ lệ (Tính năng Dự kiến)',
  'apiDocs.noteLabel': 'Lưu ý:',
  'apiDocs.rateLimitNote': 'Giới hạn tỷ lệ là tính năng dự kiến và hiện chưa được áp dụng. Sau đây là giới hạn tỷ lệ dự kiến cho mỗi cấp đăng ký.',
  'apiDocs.plannedRateLimits': 'Giới hạn tỷ lệ dự kiến theo gói đăng ký:',
  'apiDocs.freePlan': 'Gói miễn phí',
  'apiDocs.limitedApiAccess': 'Truy cập API giới hạn',
  'apiDocs.proPlan': 'Gói Pro',
  'apiDocs.standardApiAccess': 'Truy cập API tiêu chuẩn',
  'apiDocs.enterprisePlan': 'Gói Doanh nghiệp',
  'apiDocs.enhancedApiAccess': 'Truy cập API nâng cao',
  'apiDocs.rateLimitHeaders': 'Tiêu đề Giới hạn Tỷ lệ (Sắp có)',
  'apiDocs.rateLimitHeadersDesc': 'Khi được triển khai, phản hồi API sẽ bao gồm thông tin giới hạn tỷ lệ:',
  'apiDocs.codeExamples': 'Ví dụ Mã',
  'apiDocs.javascriptNode': 'JavaScript / Node.js',
  'apiDocs.jsDesc': 'Sử dụng máy khách Supabase để xác thực và fetch cho các cuộc gọi API:',
  'apiDocs.python': 'Python',
  'apiDocs.pythonDesc': 'Sử dụng máy khách Supabase để xác thực và requests cho các cuộc gọi API:',
  'apiDocs.curl': 'cURL',
  'apiDocs.needHelp': 'Cần Trợ giúp?',
  'apiDocs.needHelpDesc': 'Nếu bạn có câu hỏi hoặc cần hỗ trợ về API, chúng tôi luôn sẵn sàng giúp đỡ:',
  'apiDocs.emailLabel': 'Email:',
  'apiDocs.interactiveApiDocs': 'Tài liệu API tương tác:',
  'apiDocs.alternativeDocs': 'Tài liệu thay thế:',
  'apiDocs.facebook': 'Facebook:',
  'apiDocs.xTwitter': 'X (Twitter):',
  'apiDocs.apiVersion': 'Phiên bản API 1.0 • Cập nhật lần cuối: Tháng 1, 2025',

  // Debug Subscription
  'debug.subscriptionDebugPanel': 'Bảng gỡ lỗi đăng ký',
  'debug.refresh': 'Làm mới',
  'debug.debugCheck': 'Kiểm tra gỡ lỗi',
  'debug.currentSubStatus': 'Trạng thái đăng ký hiện tại',
  'debug.debugInformation': 'Thông tin gỡ lỗi',
  'debug.userId': 'ID người dùng:',
  'debug.email': 'Email:',
  'debug.currentPlan': 'Gói hiện tại:',
  'debug.status': 'Trạng thái:',

  // Payment Setup Guide
  'paymentSetup.stripeSecretKeyDesc': 'Khóa bí mật Stripe từ bảng điều khiển Stripe',
  'paymentSetup.webhookSecretDesc': 'Bí mật điểm cuối Webhook để xác minh sự kiện Stripe',
  'paymentSetup.createStripeAccount': 'Tạo tài khoản Stripe',
  'paymentSetup.createStripeDesc': 'Đăng ký tài khoản Stripe tại stripe.com và lấy khóa API của bạn',
  'paymentSetup.configureEnvVars': 'Cấu hình biến môi trường',
  'paymentSetup.configureEnvDesc': 'Thêm khóa Stripe vào môi trường Supabase Edge Functions',
  'paymentSetup.deployEdgeFunctions': 'Triển khai Edge Functions',
  'paymentSetup.deployEdgeDesc': 'Triển khai các hàm stripe-checkout và stripe-webhook',
  'paymentSetup.setupWebhooks': 'Thiết lập Webhook',
  'paymentSetup.setupWebhooksDesc': 'Cấu hình Stripe webhook để xử lý sự kiện thanh toán',
  'paymentSetup.testPayments': 'Kiểm tra thanh toán',
  'paymentSetup.testPaymentsDesc': 'Sử dụng thẻ thử nghiệm Stripe để xác minh luồng thanh toán',
  'paymentSetup.setupRequired': 'Yêu cầu Thiết lập Hệ thống Thanh toán',
  'paymentSetup.notConfigured': 'Hệ thống thanh toán chưa được cấu hình đầy đủ. Làm theo các bước dưới đây để bật thanh toán Stripe.',
  'paymentSetup.setupSteps': 'Các bước thiết lập',
  'paymentSetup.required': 'Bắt buộc',
  'paymentSetup.envVariables': 'Biến môi trường',
  'paymentSetup.addEnvVars': 'Thêm các biến môi trường này vào Supabase Edge Functions của bạn:',
  'paymentSetup.usefulLinks': 'Liên kết hữu ích',
  'paymentSetup.stripeApiKeysDocs': 'Tài liệu Khóa API Stripe',
  'paymentSetup.supabaseEdgeSecrets': 'Bí mật Supabase Edge Functions',
  'paymentSetup.stripeWebhooksGuide': 'Hướng dẫn Stripe Webhook',
  'paymentSetup.needHelp': 'Cần Trợ giúp?',
  'paymentSetup.needHelpDesc': 'Nếu bạn cần hỗ trợ thiết lập hệ thống thanh toán, vui lòng tham khảo tài liệu Stripe và Supabase hoặc liên hệ đội ngũ phát triển của bạn.',

  // Connection Status
  'connection.offline': 'Bạn đang ngoại tuyến. Vui lòng kiểm tra kết nối internet.',
  'connection.schemaIssue': 'Vấn đề lược đồ cơ sở dữ liệu: không tìm thấy bảng "documents".',
  'connection.dbConnectionFailed': 'Kết nối cơ sở dữ liệu thất bại. Vui lòng kiểm tra trạng thái dự án Supabase.',
  'connection.authError': 'Lỗi xác thực. Vui lòng kiểm tra cấu hình khóa API.',
  'connection.databaseError': 'Lỗi cơ sở dữ liệu: {error}',
  'connection.failedConnect': 'Không thể kết nối với máy chủ',
  'connection.connecting': 'Đang kết nối với máy chủ...',
  'connection.connectionError': 'Lỗi kết nối',
  'connection.showDetails': 'Hiển thị chi tiết',
  'connection.hideDetails': 'Ẩn chi tiết',
  'connection.connected': 'Đã kết nối',
  'connection.connectionDetails': 'Chi tiết kết nối:',
  'connection.commonFixes': 'Các cách khắc phục phổ biến:',
  'connection.fix1': 'Kiểm tra tệp .env của bạn cho VITE_SUPABASE_URL và VITE_SUPABASE_ANON_KEY',
  'connection.fix2': 'Xác minh dự án Supabase của bạn đang hoạt động (không bị tạm dừng)',
  'connection.fix3': 'Đảm bảo kết nối internet của bạn ổn định',
  'connection.fix4': 'Thử tải lại trang hoặc khởi động lại máy chủ',
};

const dictionaries: Record<Language, TranslationDict> = { en, vi };

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'appLanguage';

const detectInitialLanguage = (): Language => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'vi') return stored;
  // Auto-detect from browser locale
  const nav = navigator.language?.toLowerCase() || '';
  if (nav.startsWith('vi')) return 'vi';
  return 'en';
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(detectInitialLanguage);
  const { user } = useAuthStore();

  // Load user's stored language preference from Supabase when logged in
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('language')
          .eq('id', user.id)
          .maybeSingle();
        if (!error && data?.language && (data.language === 'en' || data.language === 'vi')) {
          setLanguageState(data.language);
          localStorage.setItem(STORAGE_KEY, data.language);
        }
      } catch (err) {
        // Non-fatal: fall back to detected/local language
        console.warn('Could not load language preference:', err);
      }
    })();
  }, [user?.id]);

  // Persist language to <html lang> for accessibility
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback(
    (lang: Language) => {
      setLanguageState(lang);
      localStorage.setItem(STORAGE_KEY, lang);

      // Persist to Supabase when signed in
      if (user?.id) {
        supabase
          .from('users')
          .update({ language: lang })
          .eq('id', user.id)
          .then(({ error }) => {
            if (error) console.warn('Could not persist language preference:', error.message);
          });
      }
    },
    [user?.id]
  );

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const dict = dictionaries[language] || dictionaries.en;
      let text = dict[key] ?? dictionaries.en[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        }
      }
      return text;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useTranslation must be used within a LanguageProvider');
  return ctx;
};
